import React, { useState, useEffect, useMemo, useRef, useId, useLayoutEffect } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { FaRegClock, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import { FiInfo } from "react-icons/fi";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { savePreTradeChecklist, getTodayPreTradeChecklist,setChecklistShown } from "../services/FirestoreService";
import { createPortal } from "react-dom";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore"; 
import { db } from "../firebase";
import SimpleCalculator from './SimpleCalculator'; 
import { FOREX_PAIRS, FOREX_INDICES, isIndexSymbol } from "../constants/instruments";
import { checkFirstTradeAnalysis } from "../services/FirstTradeAnalysisService"; // Logic
import FirstTradeAnalysisPopup from "../components/FirstTradeAnalysisPopup";     // UI

// --- Icons ---
const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2" />
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

const IconJournal = () => (
  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="#f59e42" strokeWidth="2" />
      <path d="M8 2v4M16 2v4" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

// --- Dropdowns ---
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Sydney","Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell"];
const TIMEFRAME_OPTIONS_FX = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];


// --- Products (Forex + Metals) ---
 // Build one menu (pairs + indices + your metals from this file)
 const SYMBOL_OPTIONS = [...FOREX_PAIRS, ...FOREX_INDICES
 ];

// --- Defaults ---
const defaultSettings = {
  Symbol: "",
  "Initial Balance": 2000,
  "Max Lot Size": 20,
  "Stop Loss": 30,
  "Daily Goal": 500,
  "Min Lot Size": 0.01,
  "Take Profit": 60,
  "Daily Loss Limit": 200,
  "Account Goal": 5000,
  "Running Balance": 2000,
  "Risk Percentage": 3.00,
  "Leverage": 100,
  "Account Currency": "USD",
  "Pip Value": 10, // Default for standard lot major pairs (will auto-calc)
  "Number of Trades to Goal": 0,
  "Prop Initial Balance": 2000,
  "Prop Running Balance": 2000,
  "Prop Account Goal": 5000,
  "Maximum Loss Limit": 0,
  "Drawdown Limit %": 0, 
  "Risk Mode": "%",          // "%” or “$”
  "Risk Dollar": 0,
  // Popup tracking flags
  DailyGoalPopupShownDate: "",
  LossLimitWarningShownDate: "",
  MoodVolatilityPopupShownDate: "",
  OverRiskWarningShownDate: "",
  ConsecutiveWinsPopupShownDate: "",
  AccountGoalReachedShownDate: "",
  LessonsLearnedStreakWarningShownDate: "",
  RiskOfRuinWarningShownDate: "",
  TwoLossWarningShownDate: "",
  ThreeLossWarningShownDate: "",
  OvertradingPopupShownDate: "",
  TwoLossAfterGoalWarningShownDate: "",
  Win10NoRulesPopupShownDate: "",
  Win10WithRulesPopupShownDate: "",
  PretradeChecklistShownDate: "",
  "DrawdownLockActive": false,
  "DrawdownLockStartedDate": "",
  "DrawdownLockPeak": 0,
  "Calculator Only": false,
};

const defaultJournal = {
  "Actual Balance": "",
  "Position": "",
  "Date": getTodayISO(),
  "Session": "",
  "Entry Time": "",
  "Exit Time": "",
  "Entry Price": "",
  "Exit Price": "",
  "Did I follow my Rules?": "",
  "Win": false,
  "Loss": false,
  "Mood_Mindset": "",
  "Grade": "",
  "Size": "",
  "Pips":"",
  "Potential": "",
  "Suggested Max # of Contracts": 0, // Futures only
  "Notes": "",
  "Emotional Notes": "",
  "Links_Screenshots": "",
  "Lesson": "",
  "LogLesson": false,
  "Stop Loss_Take Profit": "",
  "Percent of Balance": "",
  "Entry Strategy": "",
  "Exit Strategy": "",
  "Actual Risk to Reward": "",
  "Duration": "",
  "Trade #": "",
  "Product": "",
  "Actual Percent Growth": "",
  "Actual Reward": "",
  "Timeframe": "",
  "Target Reached": false
};

// Use the same constant in each file
const TWO_LOSS_MESSAGE = [
  "You've had 2 losses in a row.",
  "",
  "Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?",
  "",
  "Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.",
  "",
  "Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.",
  "",
  "You've got this. Apply what you learned in the next one!"
].join("\n");


// --- Helper Functions ---
function getPipValue(symbol, lots = 1, accountCurrency = "USD") {
  const sym = (symbol || "").toUpperCase();

  // Metals (unchanged)
  if (sym.startsWith("XAU")) return 1 * lots;   // $1 per pip per 1.00 lot
  if (sym.startsWith("XAG")) return 5 * lots;

  // Index CFDs – default $1 per point per 1.00 lot (typical; overrideable in UI)
  const INDEX_PIP_VALUE = {
    US30: 1, NAS100: 1, SPX500: 1, US2000: 1,
    GER40: 1, UK100: 1, FRA40: 1, JP225: 1, HK50: 1, AUS200: 1
  };
  if (INDEX_PIP_VALUE[sym] != null) return INDEX_PIP_VALUE[sym] * lots;

  // Currencies
  if (sym.includes("JPY")) return 9.09 * lots;  // approx for USD/JPY @ $9.09/lot/pip
  return 10 * lots;                              // $10 per pip per 1.00 lot
}

function getSessionFromTime(time24) {
  if (!time24) return "";
  
  const [hour] = time24.split(":").map(Number);

  // New York: 8:00 AM - 5:00 PM (17:00)
  if (hour >= 8 && hour < 17) return "New York";
  
  // London: 3:00 AM - 8:00 AM
  if (hour >= 3 && hour < 8) return "London";
  
  // Asian (Tokyo): 7:00 PM (19:00) - 3:00 AM 
  // (Using || because it crosses midnight)
  if (hour >= 19 || hour < 3) return "Asian";
  
  // Sydney: 5:00 PM (17:00) - 7:00 PM (19:00)
  // (Captures the gap after NY closes and before Tokyo opens)
  if (hour >= 17 && hour < 19) return "Sydney";
  
  return "";
}

function getPipValuePerLot(symbol) {
  const s = String(symbol || "").toUpperCase();

  // Metals (typical values; adjust to your broker if needed)
  if (s.startsWith("XAU")) return 1;   // $1 per "pip"/point per 1.00 lot
  if (s.startsWith("XAG")) return 5;   // $5 per "pip"/point per 1.00 lot

  // Indices (CFDs): start with $1 per point per 1.00 lot (easy to tune later per symbol)
  if (isIndexSymbol(s)) return 1;

  // FX
  if (s.endsWith("JPY")) return 9.09;  // ≈ $9.09 per pip per 1.00 lot for JPY quotes
  return 10;                           // majors ≈ $10 per pip per 1.00 lot
}

function getPipSizeForSymbol(symbol) {
  if (!symbol) return 0.0001;
  const s = String(symbol).toUpperCase().trim();

  // 1. Indices (CFDs) -> 1 point = 1 unit
  const indexSymbols = ["US30", "NAS100", "SPX500", "US2000", "GER40", "UK100", "FRA40", "JP225", "HK50", "AUS200"];
  if (indexSymbols.some(idx => s.includes(idx)) || (typeof isIndexSymbol === 'function' && isIndexSymbol(s))) {
      return 1;
  }

  // 2. Metals & JPY Pairs -> 0.01 per pip
  if (s.includes("JPY") || s.includes("XAU") || s.includes("GOLD") || s.includes("XAG") || s.includes("SILVER")) {
      return 0.01;
  }

  // 3. Standard Forex Pairs -> 0.0001 per pip
  return 0.0001;
}

function getEffectiveDistance(s, field) {
  const mode = s[`${field} Mode`] || "Pips";
  const val = Number(s[field] || 0);
  const entry = Number(s["Entry Price"] || 0);
  
  // Check for "Symbol" (Backtest) OR "Product" (Retail)
  const product = s["Symbol"] || s["Product"] || ""; 

  if (!val) return 0;

  if (mode === "Price" && entry > 0) {
      const pipSize = getPipSizeForSymbol(product);
      if (pipSize > 0) {
          return Math.abs(val - entry) / pipSize;
      }
  }

  if (mode === "Ticks") {
      const isIdx = getPipSizeForSymbol(product) === 1;
      return isIdx ? val / 100 : val / 10;
  }

  return val; // Mode is "Pips"
}

function computePipsFromPricesAbs(symbol, entryPrice, exitPrice) {
  // Automatically adjust for indices math (US30 = 0.1 ratio to Pips)
  const pipSize = isIndexSymbol(symbol) ? 0.1 : getPipSizeForSymbol(symbol);
  if (!pipSize || pipSize <= 0) return null;

  const eStr = String(entryPrice ?? "").trim();
  const xStr = String(exitPrice ?? "").trim();
  if (!eStr || !xStr) return null; // need both

  const entry = Number(eStr);
  const exit = Number(xStr);
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;

  const diff = Math.abs(exit - entry);
  if (!diff) return 0; // 0‑pip move is valid

  const rawPips = diff / pipSize;
  if (!Number.isFinite(rawPips)) return null;

  return Math.round(rawPips * 100) / 100;
}


function getTodayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

function useBlockWheelOnNumber(inputRef) {
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, [inputRef]);
}

function getSuggestedLotSize(balance, riskPercent, stopLoss, pipValue, minLot, maxLot) {
  const riskDollar = (Number(balance) * Number(riskPercent)) / 100;
  const perLotRisk = Number(stopLoss) * Number(pipValue);
  let rawLots = perLotRisk > 0 ? riskDollar / perLotRisk : 0;
  let lots = Math.floor(rawLots * 100) / 100; // round down to 0.01
  if (lots > maxLot) lots = maxLot;
  if (lots < minLot) lots = 0; // not possible to size safely
  return Number(lots.toFixed(2));
}

function formatCurrency(val, currency = "USD") {
  if (val === "" || val === null || typeof val === "undefined" || isNaN(val)) return "";
  return Number(val).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2
  });
}

function toPips(symbol, raw) {
  const n = Number(raw) || 0;
  const isJpy = (symbol || "").toUpperCase().includes("JPY");
  if (!isJpy) return n;                   // non-JPY: already pips

  // Heuristic: if the number is small and has a decimal, it's likely price distance
  if (n > 0 && n < 5 && String(raw).includes(".")) {
    return Math.round(n / 0.01);          // e.g., 0.30 → 30 pips
  }
  return n;                               // already pips (e.g., 20, 30)
}

const DEBUG_PROJ = false; // set false to silence logs in production

const dbg = {
  loss: (...args) => DEBUG_PROJ && console.log("%c[LOSS-PROJ]", "color:#b91c1c;font-weight:bold;", ...args),
  win:  (...args) => DEBUG_PROJ && console.log("%c[WIN -PROJ]", "color:#065f46;font-weight:bold;", ...args),
  core: (...args) => DEBUG_PROJ && console.log("%c[PROJECTIONS]", "color:#7c3aed;font-weight:bold;", ...args),
};

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>{value}</span>
    </div>
  );
}

// --- Time helpers ---
function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const ampm = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (ampm) {
    let [_, h, m, ap] = ampm;
    h = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${m}`;
  }
  return "";
}
function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}
function parseTimeToParts(str) {
  if (!str) return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return { h, m, s: s || 0 };
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [_, h, m, ap] = match;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return { h, m, s: 0 };
  }
  return null;
}
function getDurationHHMMSS(start, end) {
  const et = parseTimeToParts(start);
  const xt = parseTimeToParts(end);
  if (!et || !xt) return "";
  let startSec = et.h * 3600 + et.m * 60 + (et.s || 0);
  let endSec = xt.h * 3600 + xt.m * 60 + (xt.s || 0);
  let diff = endSec - startSec;
  if (diff < 0) diff += 24 * 3600;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}
function timeStringToSeconds(str) {
  if (!str) return 0;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return h * 3600 + m * 60 + (s || 0);
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [_, h, m, ap] = match;
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return h * 3600 + m * 60;
  }
  return 0;
}
function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map(n => String(n).padStart(2, "0")).join(":");
}

function decimalToHHMMSS(decimal) {
  if (!decimal || isNaN(decimal)) return "00:00:00";
  const totalSeconds = Math.round(Number(decimal) * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map(n => n.toString().padStart(2, "0")).join(":");
}


// Per‑lot contract size for display purposes (tweak if your broker differs)
function getContractSize(symbol = "") {
  const s = String(symbol).toUpperCase();

  // Metals
  if (s.startsWith("XAU")) return 100;
  if (s.startsWith("XAG")) return 5000;

  // ✅ Indices (CFDs) → 1 lot = 1 unit
  if (isIndexSymbol(s)) return 1;

  // Forex pairs
  return 100000;
}


function getUnitsLabel(symbol = "") {
  const s = String(symbol).toUpperCase();
  return s.startsWith("XA") ? "Ounces" : "Units";
}



function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          zIndex={10000}
          appendTo={typeof window !== "undefined" ? document.body : undefined}
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">
                On Tilt
              </div>
              <p className="mt-5  text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5  text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>

              <p className="mt-6 text-[14px] font-bold leading-relaxed">
                What to do:  
              </p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>

            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}


function Tooltip({ children, content, variant = "neutral", backdrop = false, closeOnEsc = true }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  
  const palette = {
    neutral: { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" },
    yellow: { panel: "bg-yellow-600/95 text-white ring-yellow-300/30", arrow: "bg-yellow-600/95", focus: "focus:ring-yellow-500" },
    red: { panel: "bg-red-800/95 text-white ring-red-300/30", arrow: "bg-red-800/95", focus: "focus:ring-red-500" },
    blue: { panel: "bg-slate-900/95 text-white ring-blue-300/30", arrow: "bg-slate-900/95", focus: "focus:ring-blue-500" },
  }[variant] || { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" };

  useEffect(() => {
    if (open && backdrop && closeOnEsc) {
      const handleEsc = (e) => e.key === "Escape" && setOpen(false);
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [open, backdrop, closeOnEsc]);

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={id}
        onMouseEnter={() => !backdrop && setOpen(true)}
        onMouseLeave={() => !backdrop && setOpen(false)}
        onClick={() => setOpen(!open)}
        className={`relative inline-flex items-center underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
          {backdrop && <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] pointer-events-auto" onClick={() => setOpen(false)} />}
          <div
            id={id}
            role="tooltip"
            style={{ 
              position: 'absolute', 
              top: triggerRef.current ? triggerRef.current.getBoundingClientRect().top - 10 : 0,
              left: triggerRef.current ? triggerRef.current.getBoundingClientRect().left + (triggerRef.current.getBoundingClientRect().width / 2) : 0,
              transform: 'translate(-50%, -100%)'
            }}
            className={`pointer-events-auto select-text max-w-[min(90vw,28rem)] w-max break-words rounded-lg px-4 py-3 text-[14px] leading-relaxed font-normal shadow-2xl ring-1 backdrop-blur-sm transition-opacity duration-200 opacity-100 scale-100 ${palette.panel}`}
          >
            {content}
            <span className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow -mt-1.5`} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


// === Component ===
export default function ForexCalculateForm({ userId, accountId }) {
    const [settings, setSettings] = useState({ ...defaultSettings });
    const [journal, setJournal] = useState({ ...defaultJournal });
    const [savingSettings, setSavingSettings] = useState(false);
    const [savingTrade, setSavingTrade] = useState(false);
    const [calculatedSummary, setCalculatedSummary] = useState(null);
  // projections & warnings
    const [winProjection, setWinProjection] = useState({ trades: [], tradeCount: 0 });
    const [lossProjection, setLossProjection] = useState({ trades: [], tradeCount: 0, suggestedLots: 0 });
    const [showRiskWarningPopup, setShowRiskWarningPopup] = useState(false);
    const [hasCalculated, setHasCalculated] = useState(false);
    const [entryStrategies, setEntryStrategies] = useState([]);
    const [exitStrategies, setExitStrategies] = useState([]);

    // UI state
    const [missingFields, setMissingFields] = useState([]);
    const [projectionOpen, setProjectionOpen] = useState(false);
    const [lossProjectionOpen, setLossProjectionOpen] = useState(false);
    const [showDaySeparators, setShowDaySeparators] = useState(false);
    const [progress, setProgress] = useState(0);
    const [showStrategyModal, setShowStrategyModal] = useState(false);
    const [showTradeSaved, setShowTradeSaved] = useState(false);
  // NEW — daily progress state
  const [dailyProfit, setDailyProfit] = useState(0);     // today's net profit (wins - losses), clamped at 0
  const [dailyLossAbs, setDailyLossAbs] = useState(0);   // today's realized losses (absolute)
  const [dailyLossBeyondWins, setDailyLossBeyondWins] = useState(0);
  const [postSaveCheckInfo, setPostSaveCheckInfo] = useState(null);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const { width, height } = useWindowSize();
  const hasRunInitialAutoCalc = useRef(false);
  const loadedFromServer = useRef(false);
  const [showLearnMorePopup, setShowLearnMorePopup] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  // ── Draft state for clearable Risk inputs (keeps settings numeric)
  const [draftRiskPct, setDraftRiskPct] = useState("");
  const [draftRiskDollar, setDraftRiskDollar] = useState("");
  const [editingRiskPct, setEditingRiskPct] = useState(false);
  const [editingRiskDollar, setEditingRiskDollar] = useState(false);
  const [subStatus, setSubStatus] = useState("");
  // --- NEW STATES FOR SL/TP BY PRICE & COLLAPSIBLE STYLING ---
  const [slTpMode, setSlTpMode] = useState("Pips"); // "Pips" or "Price"
  const [positionDir, setPositionDir] = useState("Long"); // "Long" or "Short"
  const [entryPriceText, setEntryPriceText] = useState("");
  const [stopPriceText, setStopPriceText] = useState("");
  const [targetPriceText, setTargetPriceText] = useState("");
  const [isBalancesExpanded, setIsBalancesExpanded] = useState(true);
  const assetClass = "Forex";

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";

  // Keep drafts in sync with settings when not actively editing
  useEffect(() => {
    if (!editingRiskPct) {
      const v = settings["Risk Percentage"];
      setDraftRiskPct(v === "" || v == null ? "" : String(v));
    }
  }, [settings["Risk Percentage"], editingRiskPct]);

  useEffect(() => {
    if (!editingRiskDollar) {
      const v = settings["Risk Dollar"];
      setDraftRiskDollar(v === "" || v == null ? "" : String(v));
    }
  }, [settings["Risk Dollar"], editingRiskDollar]);

  useEffect(() => {
  setJournal(prev => {
    const next = { ...prev };
    let changed = false;

    // Helper to apply default if field is empty
    const applyDefault = (field, settingKey) => {
      const defVal = String(settings[settingKey] || "").trim();
      if (defVal && !String(next[field] || "").trim()) {
        next[field] = defVal;
        changed = true;
      }
    };

    applyDefault("Entry Strategy", "Default Entry Strategy");
    applyDefault("Exit Strategy", "Default Exit Strategy");
    applyDefault("Timeframe", "Default Timeframe");
    applyDefault("Session", "Default Session"); // ✅ ADDED THIS LINE

    return changed ? next : prev;
  });
}, [
  settings["Default Entry Strategy"], 
  settings["Default Exit Strategy"], 
  settings["Default Timeframe"],
  settings["Default Session"] // ✅ ADDED THIS DEPENDENCY
]);

// --- AUTO-CALCULATE PIPS WHEN IN "PRICE" MODE ---
  useEffect(() => {
    if (slTpMode === "Price" && settings["Symbol"]) {
      const sym = settings["Symbol"].toUpperCase();
      let pipSize = getPipSizeForSymbol(sym);
      
      if (!pipSize) {
        if (sym.includes("XAU") || sym.includes("XAG") || isIndexSymbol(sym)) pipSize = 1;
        else pipSize = sym.includes("JPY") ? 0.01 : 0.0001;
      }
      
      const ep = Number(entryPriceText);
      const sp = Number(stopPriceText);
      const tp = Number(targetPriceText);

      if (ep > 0 && sp > 0) {
        const slPips = Math.abs(ep - sp) / pipSize;
        handleSettingChange("Stop Loss", Math.round(slPips));
      } else {
        handleSettingChange("Stop Loss", "");
      }

      if (ep > 0 && tp > 0) {
        const tpPips = Math.abs(tp - ep) / pipSize;
        handleSettingChange("Take Profit", Math.round(tpPips));
      } else {
        handleSettingChange("Take Profit", "");
      }
    }
  }, [entryPriceText, stopPriceText, targetPriceText, positionDir, settings["Symbol"], slTpMode]);


  // --- Auto-sync Size, Entry, and Exit Prices to Journal (FOREX ONLY) ---
useEffect(() => {
    const entryPrice = Number(settings["Entry Price"] || 0);
    const tpVal = Number(settings["Take Profit"] || 0);
    const position = journal["Position"]; 
    let estTargetPrice = "";
    
    if (entryPrice > 0 && tpVal > 0) {
        if (settings["Take Profit Mode"] === "Price") {
            estTargetPrice = tpVal;
        } else {
            // FIX: Use settings["Symbol"] instead of "Product"
            const pipSize = getPipSizeForSymbol(settings["Symbol"]) || 0.0001;
            const isIdx = isIndexSymbol(settings["Symbol"]);
            let ptsDist = tpVal;
            
            // Adjust math if they selected "Ticks" instead of "Pips"
            if (settings["Take Profit Mode"] === "Ticks") {
                ptsDist = isIdx ? tpVal / 100 : tpVal / 10;
            }
            
            // FIX: Account for Buy vs Sell direction
            if (position === "Sell") {
                estTargetPrice = Number((entryPrice - ptsDist * pipSize).toFixed(isIdx ? 2 : 5));
            } else {
                // Default to Buy (adding distance)
                estTargetPrice = Number((entryPrice + ptsDist * pipSize).toFixed(isIdx ? 2 : 5));
            }
        }
    }

    setJournal(prev => {
        const next = { ...prev };
        let changed = false;

        // 1. Auto-sync the Suggested Lot Size
        if (calculatedSummary && calculatedSummary.size !== undefined) {
          if (prev["Size"] === undefined || prev["Size"] === "") {
            next["Size"] = calculatedSummary.size;
            changed = true;
          }
        }
        
        // 2. Auto-sync Entry Price
        if (settings["Entry Price"] !== undefined && settings["Entry Price"] !== "") {
            if (next["Entry Price"] !== settings["Entry Price"]) {
                next["Entry Price"] = settings["Entry Price"];
                changed = true;
            }
        }
        
        // 3. Auto-sync Exit Price
        if (estTargetPrice !== "") {
            if (next["Exit Price"] !== estTargetPrice) {
                next["Exit Price"] = estTargetPrice;
                changed = true;
            }
        }
        
        return changed ? next : prev;
    });
}, [settings["Entry Price"], settings["Take Profit"], settings["Take Profit Mode"], settings["Symbol"], calculatedSummary, journal["Position"]]);

  // Commit helpers
  const commitRiskPct = () => {
    const s = String(draftRiskPct).trim();
    if (s === "") { setEditingRiskPct(false); return; }
    let n = Number(s);
    if (!Number.isFinite(n) || n <= 0) n = 0.01;  // must be > 0, no upper cap
    handleSettingChange("Risk Percentage", n);
    setEditingRiskPct(false);
  };

  const commitRiskDollar = () => {
    const s = String(draftRiskDollar).trim();
    if (s === "") { setEditingRiskDollar(false); return; }
    let n = Number(s);
    if (!Number.isFinite(n) || n <= 0) n = 0.01;  // must be > 0, no upper cap
    handleSettingChange("Risk Dollar", n);
    setEditingRiskDollar(false);
  };

  // derived
  const stopLoss = Number(settings["Stop Loss"]);
  const riskPercent = Number(settings["Risk Percentage"]);
  const runningBalance = Number(settings["Running Balance"]);
  const minLot = Number(settings["Min Lot Size"]) || 0.01;
  const maxLot = Number(settings["Max Lot Size"]) || 100;
  const leverage = Number(settings["Leverage"]) || 100;
  const pipValue = Number(settings["Pip Value"]) || getPipValue(settings["Symbol"], 1, settings["Account Currency"]);
  const riskDollar = runningBalance * riskPercent / 100;


  // Helper: prefer first defined value
const pick = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

// --- Display bases (what you show in the tables' Balance columns) ---
const runningDisplay = Number(pick(settings, "Running Balance")) || 0;
const goalDisplay    = Number(pick(settings, "Account Goal"))   || 0;

// --- Prop bases (what you use for all *calculations*) ---
const propRunning = Number(pick(settings, "Prop Running Balance", "propRunningBalance", "Running Balance")) || 0;
const propGoal    = Number(pick(settings, "Prop Account Goal",   "propAccountGoal",   "Account Goal"))     || 0;

const canComputeLossProj = stopLoss > 0 && pipValue > 0 && riskPercent > 0;
const accountIsBlown = canComputeLossProj && (lossProjection?.tradeCount ?? 0) === 0;

  // Popup Queue States
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null); // { id: 'popupName', data: {...} }
  const [showChecklist, setShowChecklist] = useState(false);
  const [userPrefs, setUserPrefs] = useState(() => {
    // Initialize synchronously from localStorage to prevent balance flashing
    const cached = localStorage.getItem("isPrivacyMode");
    return cached === "true" ? { "Privacy Mode": true } : {};
  });
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const popupsAllowedRef = useRef(false);
  const dailyGoalAmount = Number(settings["Daily Goal"] || 0);
  const dailyLossLimitAmount = Number(settings["Daily Loss Limit"] || 0);
  // Clears summary and related UI
const resetSummaryUi = React.useCallback(() => {
  setCalculatedSummary(null);
  setWinProjection({ trades: [], tradeCount: 0 });
  setLossProjection({ trades: [], tradeCount: 0, suggestedContracts: 0 });
  setJournal(prev => ({ ...prev, Size: "" }));  // clear suggested/typed size
  setHasCalculated(false);
}, []);



  const dailyGoalProg = {
    done: Number(dailyProfit) || 0,          // today's net profit (>=0)
    target: dailyGoalAmount,                 // settings["Daily Goal"]
    pct:
      dailyGoalAmount > 0
        ? Math.min(100, Math.max(0, (Number(dailyProfit) / dailyGoalAmount) * 100))
        : 0
  };

  const [mllDirty, setMllDirty] = useState(false);

// Clears summary & both projections until the user clicks Calculate
function resetSummaryAndProjections() {
  setCalculatedSummary(null);
  setWinProjection({ trades: [], tradeCount: 0 });
  setLossProjection({ trades: [], tradeCount: 0, suggestedLots: 0, blownAt: 0 });
  setHasCalculated(false);
}

const dailyLossProg = {
  lost: Number(dailyLossAbs) || 0,   // now = net loss today (only if net < 0)
  limit: dailyLossLimitAmount,
  pct:
    dailyLossLimitAmount > 0
      ? Math.min(100, Math.max(0, (Number(dailyLossAbs) / dailyLossLimitAmount) * 100))
      : 0
};

const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });

useEffect(() => {
  if (!userId) return;

  const ref = doc(db, "users", userId, "settings", "userSettings");
  const unsub = onSnapshot(ref, snap => {
    const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                               : { futures: [], forex: [] };
    setUserFavorites(favs);
  });

  return unsub; // cleanup
}, [userId]);

const favoriteForexOptions = useMemo(() => {
  const fav = (userFavorites?.forex || []).map(s => s.toUpperCase());
  if (!fav.length) return [];
  return fav
    .map(sym => SYMBOL_OPTIONS.find(([v]) => String(v).toUpperCase() === sym))
    .filter(Boolean);
}, [userFavorites, SYMBOL_OPTIONS]);

const favoriteSymbolValues = useMemo(
  () => new Set(favoriteForexOptions.map(([v]) => v)),
  [favoriteForexOptions]
);

const nonFavoriteSymbols = useMemo(
  () => SYMBOL_OPTIONS.filter(([v]) => !favoriteSymbolValues.has(v)),
  [favoriteSymbolValues]
);


// === Checks ===
  async function checkConsecutiveWins(
    trades,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const today = getTodayLocalISO();

    // Only look at today's trades
    const todayTrades = (trades || []).filter(t => (t["Date"] || "").startsWith(today));
    if (todayTrades.length < 5) return;

    // Be tolerant if "Win" can be string/boolean
    const isWin = v => v === true || String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

    // Check if the last 5 trades are wins
    const last5 = todayTrades.slice(-5);
    const popupId = "consecutiveWinsPopup";
    const shownDateKey = "ConsecutiveWinsPopupShownDate";

    // Only queue the popup if the last 5 trades are wins AND it hasn't been shown today
    if (last5.every(t => isWin(t["Win"])) && currentSettings[shownDateKey] !== today) {
      queuePopup(popupId);
      const newSettings = { [shownDateKey]: today };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(prev => ({ ...prev, ...newSettings }));
    }
    // The "else if" block that previously reset the flag for the same day has been removed.
  }

  async function checkOvertrading(trades, maxTradesPerDay, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
    const today = getTodayLocalISO();
    const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
    const popupId = "overtradingPopup";
    const shownDateKey = "OvertradingPopupShownDate";

    if (todayTrades.length > maxTradesPerDay && currentSettings[shownDateKey] !== today) {
      queuePopup(popupId, { maxTradesPerDay });
     
      await updateSettingsFunc(userId, accountId, { [shownDateKey]: today });     // patch-only
      setLocalSettings(prev => ({ ...prev, [shownDateKey]: today }));
    } else if (todayTrades.length <= maxTradesPerDay && currentSettings[shownDateKey] === today) {
       await updateSettingsFunc(userId, accountId, { [shownDateKey]: "" });
      setLocalSettings(prev => ({ ...prev, [shownDateKey]: "" }));
    }
  }

  // Optional: set your preferred time zone here (or pass one in when calling).
// For US markets you might prefer: 'America/New_York'
// Leave as undefined to use the user's local time.
const DEFAULT_TIME_ZONE = undefined;

function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    ...(timeZone ? { timeZone } : {})
  }).format(now);

  if (weekday === 'Sunday' || weekday === 'Monday') {
    return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Tuesday' || weekday === 'Wednesday') {
    return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Thursday') {
    return "Go and enjoy the rest of your day!";
  }
  if (weekday === 'Friday') {
    return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
  }
  // Saturday or anything unexpected — fallback
  return "Go and enjoy the rest of your day!";
}


  async function checkTooManyLosses(
    trades,
    maxLossesPerDay,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const today = getTodayLocalISO();

    // Be tolerant of boolean/strings in the "Loss" field
    const isLoss = (v) => String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

    const todayLosses = trades.filter(
      (t) => (t["Date"] || "").startsWith(today) && isLoss(t["Loss"])
    );

    const popupId = "tooManyLossesPopup";
    const shownDateKey = "TooManyLossesPopupShownDate";

    // Fire when the limit is REACHED (>=), not only when it's exceeded
    if (todayLosses.length >= maxLossesPerDay && currentSettings[shownDateKey] !== today) {
      queuePopup(popupId, { maxLossesPerDay });
      await updateSettingsFunc(userId, accountId, { [shownDateKey]: today });     // patch-only
      setLocalSettings(prev => ({ ...prev, [shownDateKey]: today }));
    }
    // Reset once you're back UNDER the limit

  }

  // Reset summary if no Product is selected (page load or any time it becomes empty)
  useEffect(() => {
    if (!settings?.Symbol) {
      resetSummaryUi();

    }
  }, [settings?.Symbol, resetSummaryUi]);


async function checkMoodVolatility(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();
  const shownDateKey = "MoodVolatilityPopupShownDate";

  // ✅ Defensive: if this flag ever got corrupted to an array, reset it.
  if (Array.isArray(currentSettings?.[shownDateKey])) {
    await updateSettingsFunc(userId, accountId, { [shownDateKey]: "" });
    setLocalSettings(prev => ({ ...prev, [shownDateKey]: "" }));
  }

  // Work only with today's trades
  const todayTrades = Array.isArray(trades)
    ? trades.filter(t => (t["Date"] || "").startsWith(todayISO))
    : [];

  if (todayTrades.length < 3) return;

  const last3 = todayTrades.slice(-3);

  // Be tolerant of strings; support alternate keys if ever present
  const moods = last3.map(t => {
    const v = Number(t["Mood_Mindset"] ?? t["Mood"] ?? t["Mood / Mindset"]);
    return Number.isFinite(v) ? v : null;
  });

  // Trigger if all 3 are 4 or 5
  const allHigh = moods.every(v => v !== null && (v === 4 || v === 5));

  if (allHigh && currentSettings[shownDateKey] !== todayISO) {
    queuePopup("moodVolatilityPopup");
    const patch = { [shownDateKey]: todayISO };          // ✅ store a DATE STRING
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  } else if (!allHigh && currentSettings[shownDateKey] === todayISO) {
    const patch = { [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}


  {currentPopup?.id === 'drawdownWarning' && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          onClick={closeCurrentPopup}
          aria-label="Close"
        >×</button>
        <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
          <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
          </svg>
          Drawdown Warning
        </h2>
        <p className="mb-4 text-gray-800 dark:text-gray-100">
          <span className="font-bold text-purple-700 dark:text-purple-300">
            Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
          </span>
        </p>
        <p className="mb-4 text-gray-800 dark:text-gray-100">
          <span className="font-bold">Drawdown amount: </span>
          <span className="font-bold text-purple-700 dark:text-purple-300">
            ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
          </span>
          <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
            ({currentPopup.data.drawdownPct?.toFixed(2)}%)
          </span>
        </p>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          Consider pausing to review your trades and protect your capital before proceeding.
        </p>
        <p className="mt-6 mb-6 text-gray-700 dark:text-gray-300">
          No one wins 100% of the time but there is nothing wrong with switching to demo until you are winning consistently and growing your account.
        </p>
        <button
          onClick={closeCurrentPopup}
          className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  )}

  // NEW — compute today's net profit and realized losses from journal rows
function computeDailyProgressFromRows(rows = []) {
  const today = getTodayLocalISO();
  const todays = rows.filter(t => (t["Date"] || "").startsWith(today));

const netProfit = todays.reduce((sum, r) =>
  sum + (Number(r["Actual Reward"]) || 0), 0);

setDailyProfit(Math.max(0, netProfit));
setDailyLossAbs(Math.max(0, -netProfit)); // <= only loss beyond today’s wins



  // 🔴 Red bar (daily loss limit): only the amount by which losses exceed wins
  // If netProfit >= 0, this is 0; if netProfit < 0, it's the absolute shortfall
  setDailyLossBeyondWins(Math.max(0, -netProfit));
}

async function updateDailyProgress(rowsOpt) {
  try {
    if (Array.isArray(rowsOpt)) {
      computeDailyProgressFromRows(rowsOpt);
    } else {
      const rows = await fetchJournalRows(userId, accountId);
      computeDailyProgressFromRows(rows);
    }
  } catch (e) {
    console.error("Failed to update daily progress", e);
  }
}


// Keep this with your other helper functions (AFTER updateDailyProgress, BEFORE checkWin10NoRulesPopup)
async function checkDailyProfitTarget(
  trades,
  dailyGoal,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();
  const popupId = "dailyGoalPopup";
  const shownDateKey = "DailyGoalPopupShownDate";
  const shownGoalKey = "DailyGoalPopupShownForGoal";

  // Work only with today's trades (tolerant of missing/empty trades)
  const todayTrades = Array.isArray(trades)
    ? trades.filter(t => (t?.["Date"] || "").startsWith(today))
    : [];

  const totalProfit = todayTrades.reduce(
    (sum, t) => sum + (Number(t?.["Actual Reward"]) || 0),
    0
  );

  const alreadyShownForThisGoalToday =
    currentSettings?.[shownDateKey] === today &&
    Number(currentSettings?.[shownGoalKey] || 0) === Number(dailyGoal || 0);

  // ── A) If it was shown earlier today for this goal and we've now fallen BELOW the goal,
  //        clear ONLY the tracking fields (but don't clear on the “empty array on load” case).
/*  if (alreadyShownForThisGoalToday && todayTrades.length > 0 && totalProfit < dailyGoal) {
    await updateSettingsFunc(userId, accountId, {
      [shownDateKey]: "",
      [shownGoalKey]: 0
    });
    setLocalSettings(prev => ({ ...prev, [shownDateKey]: "", [shownGoalKey]: 0 }));
    return;
  }*/

  // ── B) If it hasn't been shown for this goal today and we've now REACHED the goal, show it and set flags.
  if (!alreadyShownForThisGoalToday && dailyGoal > 0 && totalProfit >= dailyGoal) {
    queuePopup(popupId, {
      message: "Congratulations on hitting your daily goal!",
      dailyGoal
    });

    await updateSettingsFunc(userId, accountId, {
      [shownDateKey]: today,
      [shownGoalKey]: Number(dailyGoal)
    });
    setLocalSettings(prev => ({ ...prev, [shownDateKey]: today, [shownGoalKey]: Number(dailyGoal) }));
  }

  // Otherwise, do nothing.
}



// helper booleans for tolerant checks
function isYes(v) {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "yes" || s === "true" || s === "y" || s === "1";
}
function isNo(v) {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "no" || s === "false" || s === "n" || s === "0";
}

async function checkWin10NoRulesPopup(
  trade,
  settings,
  updateSettings,
  userId,
  accountId,
  setLocalSettings,
  queuePopup
) {
  // Use local day
  const today = getTodayLocalISO();
  const key = "Win10NoRulesPopupShownDate";

  // Numbers we need
  const reward = Number(trade["Actual Reward"] || 0);
  const propRBNow = Number(settings["Prop Running Balance"] || 0);
  const prevPropRB = propRBNow - reward;

  // Prefer computing from prop balances; fall back to the saved string if needed
  let growthPct = 0;
  if (prevPropRB > 0) {
    growthPct = (reward / prevPropRB) * 100;
  } else {
    const parsed = Number(String(trade["Actual Percent Growth"] || "").replace("%", ""));
    growthPct = Number.isFinite(parsed) ? parsed : 0;
  }

  // Tolerant win check
  const winTrue =
    trade["Win"] === true || isYes(trade["Win"]) || reward > 0;

  // Rules check (supports both key spellings)
  const rulesAnswer = trade["Did I follow my Rules?"] ?? trade["Did I follow my Rules"];
  const rulesNo = isNo(rulesAnswer);

  if (winTrue && growthPct >= 10 && rulesNo && settings[key] !== today) {
    queuePopup("win10NoRulesPopup", {
        message: "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!",
      });
    const patch = { [key]: today };
    await updateSettings(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  } else if (( !winTrue || growthPct < 10 || !rulesNo ) && settings[key] === today) {
    // reset flag if conditions no longer match
    const patch = { [key]: "" };
    await updateSettings(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}

  async function checkAccountGoalReached(
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();
  const shownKey = "AccountGoalReachedShownDate";
  const rb   = Number(currentSettings["Running Balance"] || 0);
  const goal = Number(currentSettings["Account Goal"]   || 0);

  if (goal > 0 && rb >= goal && currentSettings[shownKey] !== today) {
    queuePopup("accountGoalReached", {
      runningBalance: rb,
      accountGoal: goal
    });
    const patch = { [shownKey]: today };
    await updateSettingsFunc(userId, accountId, patch);   // patch only the flag
    setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}

  async function checkDailyLossLimit(trades, currentSettings, updateSettings, userId, accountId, setSettings) {
    const today = getTodayLocalISO();
    const lossLimit = Number(currentSettings["Daily Loss Limit"] || 0);
    if (lossLimit <= 0) return; // No limit set

    const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
    const netProfit = todayTrades.reduce((sum, t) => sum + (Number(t["Actual Reward"]) || 0), 0);
    
    // This calculates loss only after all wins for the day have been wiped out
    const lossBeyondWins = Math.max(0, -netProfit);

    const shownDateKey = "DailyLossLimitPopupShownDate";
    if (lossBeyondWins >= lossLimit && currentSettings[shownDateKey] !== today) {
      queuePopup("dailyLossLimitExceeded", { 
        limit: lossLimit, 
        lostBeyondWins: lossBeyondWins 
      });
      // Use a partial update to only change the flag
      const update = { [shownDateKey]: today };
      await updateSettings(userId, accountId, update);
      setSettings(prev => ({ ...prev, ...update }));
    }
  }


async function checkWin10WithRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
  const today = new Date().toISOString().slice(0, 10);
  const key = "Win10WithRulesPopupShownDate";
  const winTrue = trade["Win"] === true;
  const pct = Number(trade["Actual Percent Growth"]?.replace("%", ""));
  const rulesYes = (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "Yes";

  if (winTrue && pct >= 20 && rulesYes && settings[key] !== today) {
    queuePopup("win10WithRulesPopup", {
      message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!",
      confetti: true
    });
   const patch = { [key]: today };
   await updateSettings(userId, accountId, patch);
   setLocalSettings(prev => ({ ...prev, ...patch }));
  } else if ((!winTrue || pct < 20 || !rulesYes) && settings[key] === today) {
   const patch = { [key]: "" };
   await updateSettings(userId, accountId, patch);
   setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}


function computeProjections(s) {
  const displayBalance = Number(s["Running Balance"] || 0);
  const displayGoal    = Number(s["Account Goal"] || s["Prop Account Goal"] || 0);

  const calcStart = Number(s["Prop Running Balance"] ?? s["Running Balance"] ?? displayBalance);
  const calcGoal  = Number(s["Prop Account Goal"] ?? displayGoal);

  const riskPercent = Number(s["Risk Percentage"] || 0);
  
  // FIX: Convert to distance in pips instead of raw values
  const stopLoss    = getEffectiveDistance(s, "Stop Loss");
  const takeProfit  = getEffectiveDistance(s, "Take Profit");
  
  const minLot      = Number(s["Min Lot Size"] || 0.01);
  const maxLot      = Number(s["Max Lot Size"] || 100);
  const pipValue    = Number(s["Pip Value"]) || getPipValue(s["Product"], 1, s["Account Currency"]);
  const riskMode    = getRiskMode(s);
  const riskDollarFixed = Number(s["Risk Dollar"] || 0);

  const perLotRisk = stopLoss * pipValue;
  const startBudget = riskMode === "$"
    ? riskDollarFixed
    : (calcStart * riskPercent) / 100;

  let lots = 0;
  if (perLotRisk > 0 && startBudget > 0) {
    let rawLots = startBudget / perLotRisk;
    rawLots = Math.max(minLot, Math.min(maxLot, rawLots));
    lots = Math.floor(rawLots / 0.01) * 0.01;
    if (lots <= 0) lots = minLot;
  }

  const perTradeProfit  = lots * pipValue * takeProfit;
  const actualRiskDollar = lots * perLotRisk;

  // Win: compounding by default (for % mode this grows with balance; for $ it's constant)
  const win = getWinProjectionPropBasis({
    calcStart,
    calcGoal,
    displayStart: displayBalance,
    riskPercent,
    riskMode,
    riskDollarFixed,
    stopLoss,
    takeProfit,
    minLot,
    maxLot,
    pipValue,
    compound: true
  });

  // Loss: stop at blown threshold using whichever risk sizing mode is active
  const initialForBlow = Number(s["Prop Initial Balance"] || s["Initial Balance"] || 0);
  const maxLossInput   = Number(s["Maximum Loss Limit"] || 0);

  const loss = getLossProjectionPropBasis({
    calcStart,
    displayStart: displayBalance,
    riskPercent,
    riskMode,
    riskDollarFixed,
    stopLoss,
    minLot,
    maxLot,
    pipValue,
    baseBalance:  initialForBlow,
    maxLossLimit: maxLossInput
  });

  return {
    win,
    loss,
    lots,
    totalRisk: actualRiskDollar,
    targetReward: perTradeProfit
  };
}




async function checkLessonsLearnedStreak(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();

  // ✅ Only count trades explicitly logged to the lesson log
  const lessonsToday = (trades || []).filter(t => {
    const d = t["Date"];
    let isToday = false;

    if (!d) return false;
    if (typeof d === "string") {
      isToday = d.slice(0, 10) === todayISO || d.startsWith(todayISO);
    } else if (d?.seconds) {
      isToday = new Date(d.seconds * 1000).toISOString().slice(0, 10) === todayISO;
    } else {
      try { isToday = new Date(d).toISOString().slice(0, 10) === todayISO; } catch { isToday = false; }
    }
    if (!isToday) return false;

    // ✅ Require that the lesson was actually logged (LogLesson = true)
    const loggedFlag = t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true";
    return loggedFlag;
  }).length;

  if (lessonsToday < 3) {
    // If previously shown but now below threshold, reset
    if (currentSettings["LessonsLearnedStreakWarningShownDate"] === todayISO) {
      const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: "" };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings(next);
    }
    return;
  }

  // Fire once per day when the threshold (>= 3) is reached
  if (currentSettings["LessonsLearnedStreakWarningShownDate"] !== todayISO) {
    queuePopup("lessonsLearnedStreakWarning", { lessonsToday });
    const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: todayISO };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  }
}



async function checkRiskOfRuin(
  lossProjection,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();
  const popupId = "riskOfRuinWarning";
  const shownDateKey = "RiskOfRuinWarningShownDate";

  // Prefer Prop balances on the prop form; fall back to retail if needed
  const initial = Number(
    (currentSettings["Prop Initial Balance"] ?? currentSettings["Initial Balance"]) || 0
  );
  const balance = Number(
    (currentSettings["Prop Running Balance"] ?? currentSettings["Running Balance"]) || 0
  );

  if (!Number.isFinite(initial) || initial <= 0) return;
  const threshold = initial * 0.25;

  if (balance <= threshold && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId, {
      initial,
      balance,
      threshold,
      tradeCount: lossProjection?.tradeCount
    });

    const newSettings = { ...currentSettings, [shownDateKey]: today };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (balance > threshold && currentSettings[shownDateKey] === today) {
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkTwoLossAfterGoal(
  trades,
  settings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  queuePopupFunc
) {
  const today = getTodayLocalISO();
  const shownKey = "TwoLossAfterGoalWarningShownDate";
  if (settings[shownKey] === today) return; // already shown today

  const dailyGoal = Number(settings["Daily Goal"] || 0);


    const todayTrades = (trades || []).filter(t => (t["Date"] || "").startsWith(today));

     let runningProfit = 0;
     let goalReachedAtIndex = -1;

     for (let i = 0; i < todayTrades.length; i++) {
       runningProfit += Number(todayTrades[i]["Actual Reward"] || 0);
       if (runningProfit >= dailyGoal && goalReachedAtIndex === -1) {
         goalReachedAtIndex = i;
       }
     }
     if (goalReachedAtIndex === -1) return;


    const tradesAfterGoal = todayTrades.slice(goalReachedAtIndex + 1);
     // Trigger on any later pair of consecutive losses after the goal
     let twoLosses = false;
     let streak = 0;
     for (const t of tradesAfterGoal) {
       if (isTrueLoss(t["Loss"], t["Actual Reward"])) {
         streak += 1;
         if (streak >= 2) { twoLosses = true; break; }
       } else {
       streak = 0;
       }
     }
    if (twoLosses && settings[shownKey] !== today) {
       queuePopupFunc("twoLossAfterGoalWarning", {
         message:
           "You have taken 2 losses after reaching your daily goal.\n\nYou could easily go on tilt if you continue to lose trying to get it back. \n\nConsider stopping for the day to protect your gains."
       });

      const patch = { [shownKey]: today };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings(prev => ({ ...prev, ...patch }));
    }
     }


  // Popup helpers
  // Function to add a popup to the queue (guarded) with priority handling
const queuePopup = (id, data = {}) => {
  if (!popupsAllowedRef.current) return;      // gate popups unless allowed (e.g., during save)
  setPopupQueue(prev => {
    let next = prev;

    // Priority: after-goal 2‑loss warning hides the generic 2‑loss warning
    if (id === 'twoLossAfterGoalWarning') next = next.filter(p => p.id !== 'twoLossWarning');

    // If higher‑priority is pending/showing, block the generic one
    if (id === 'twoLossWarning') {
      const higherPending = next.some(p => p.id === 'twoLossAfterGoalWarning');
      const higherShowing = currentPopup?.id === 'twoLossAfterGoalWarning';
      if (higherPending || higherShowing) return next;
    }

    // De‑dup: don’t re‑add if queued or currently visible
    if (next.some(p => p.id === id) || currentPopup?.id === id) return next;
    return [...next, { id, data }];
  });
};


// Normalize to "YYYY-MM-DD"
 // new: always produce a LOCAL YYYY-MM-DD key, including Firestore Timestamps
 function localISODay(d) {
   const dt = new Date(d);
   const tz = dt.getTimezoneOffset() * 60000; // minutes → ms
   return new Date(dt - tz).toISOString().slice(0, 10);
 }

 function normalizeDateKey(v) {
   if (!v) return "";
   if (typeof v === "string") {
     const s = v.trim();
     if (s.includes("/")) {                 // "MM/DD/YYYY" → local ISO
       const [mm, dd, yyyy] = s.split("/");
       return `${yyyy}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`;
     }
     return s.slice(0, 10);                 // "YYYY-MM-DD..." already fine
   }
   if (v?.seconds) return localISODay(v.seconds * 1000); // Firestore TS → local day
   try { return localISODay(v); } catch { return ""; }
 }


function closeCurrentPopup() {
  setCurrentPopup(null);
}

// --- Manage the popup queue ---
useEffect(() => {
  if (currentPopup === null && popupQueue.length > 0) {
    const nextPopup = popupQueue[0];
    setPopupQueue(prev => prev.slice(1));
    setCurrentPopup(nextPopup);
  }
}, [popupQueue, currentPopup]);

useEffect(() => {
    if (!userId) return;
    getUserSettings(userId).then(data => {
      // If the prefs doc doesn't exist (data = {}), set default prefs!
      if (!data || Object.keys(data).length === 0) {
        // Pick your defaults here
        const defaultPrefs = {
          "Pretrade Checklist": "Yes",   // Or "No" if you want off by default
          "PretradeChecklistShownDate": ""
          // ...add any other default user prefs you want here
        };
        setUserPrefs(defaultPrefs);
        // ✅ Cache default to local storage
        localStorage.setItem("isPrivacyMode", "false"); 
        updateUserSettings(userId, defaultPrefs); // Save to Firestore
      } else {
        setUserPrefs(data);
        // ✅ Cache fetched setting to local storage
        localStorage.setItem("isPrivacyMode", data["Privacy Mode"] === true ? "true" : "false"); 
      }
    });
  }, [userId]);

  const openedForTodayRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const today   = getTodayLocalISO();
    const enabled = ["yes","true","on","1"].includes(String(userPrefs?.["Pretrade Checklist"] ?? "").toLowerCase());
    const shown   = normalizeDateKey(
      userPrefs?.["PretradeChecklistShownDate"] ?? userPrefs?.["pretradeChecklistLastShown"] ?? ""
    );

    // If it's a new day relative to the last stamp, reset the gate so we can show again.
    if (shown !== today) {
      openedForTodayRef.current = false;
    } else {
      // Already stamped for today → make sure it's closed and mark handled.
      setShowChecklist(false);
      openedForTodayRef.current = true;
      return;
    }

    if (enabled && !openedForTodayRef.current) {
      setShowChecklist(true);
      openedForTodayRef.current = true; // prevent re-open if userPrefs reflows
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    userPrefs?.["Pretrade Checklist"],
    userPrefs?.["PretradeChecklistShownDate"],  // canonical (user-level)
    userPrefs?.["pretradeChecklistLastShown"]   // legacy (read-only fallback)
  ]);

  useEffect(() => {
    setJournal(prev => {
      // If user already has a value (or editing an existing trade), do not override.
      const cur = String(prev["Timeframe"] || "").trim();
      if (cur) return prev;

      const defTf = String(settings?.["Default Timeframe"] || "").trim();
      return defTf ? { ...prev, "Timeframe": defTf } : prev;
    });
  }, [settings?.["Default Timeframe"]]); 


  useEffect(() => {
  if (!userId || !accountId) return;
  updateDailyProgress();
}, [userId, accountId]);

useEffect(() => {
  if (!settings?.Symbol) {
    setCalculatedSummary(null);
    setWinProjection({ trades: [], tradeCount: 0 });        // state you already set after calculate
    setLossProjection({ trades: [], tradeCount: 0, suggestedContracts: 0 });
    setJournal(prev => ({ ...prev, Size: "" }));            // clear suggested/typed size
    setHasCalculated(false);                                 // keep your “Inactive” pill logic correct
  }
}, [settings?.Symbol]);

// Keep the summary cleared while Product is not selected
useEffect(() => {
  if (!settings["Symbol"]) {
    setCalculatedSummary(null);
  }
}, [settings["Symbol"]]);


useEffect(() => {
  if (!settings["Symbol"]) {
    setCalculatedSummary(null);
    setHasCalculated(false);
    setWinProjection({ trades: [], tradeCount: 0 });
    setLossProjection({ trades: [], tradeCount: 0, suggestedLots: 0 });
    setJournal(prev => ({ ...prev, Size: "" })); // avoids saving stale lot size
  }
}, [settings["Symbol"]]);

// --- Auto-Save Settings to Database ---
   useEffect(() => {
     // Don't overwrite the database before the initial load is complete
     if (!loadedFromServer.current) return; 

     // Wait 1 second after the user stops typing to save to Firebase
     const delayDebounceFn = setTimeout(() => {
       updateSettings(userId, accountId, sanitizeSettingsForPersist(settings))
         .catch(err => console.error("Auto-save failed:", err));
     }, 1000); 

     return () => clearTimeout(delayDebounceFn);
   }, [settings, userId, accountId]);


 async function refreshAfterSave(userId, accountId) {
  const fresh = await getSettings(userId, accountId);
  const pv = Number(fresh["Pip Value"]) || getPipValue(fresh["Symbol"], 1, fresh["Account Currency"]);

   setSettings(prev => ({
     ...prev,
     ...fresh,
     ...(mllDirty ? { "Maximum Loss Limit": prev["Maximum Loss Limit"] } : {})
   }));

   if (mllDirty) {
     resetSummaryAndProjections();
     return;
   }

  // --- One place for all math ---
  const { win, loss, lots, totalRisk, targetReward } = computeProjections(fresh);

  // --- Keep summary in sync with what the engine used ---
  const minL = Number(fresh["Min Lot Size"] || 0.01);
  const baseForPercent = Number(fresh["Prop Running Balance"] ?? fresh["Running Balance"] ?? 0);
  const riskBudget = riskBudgetDollarForBalance(fresh, baseForPercent);
  const riskPercentDisplay = riskPercentFromBudget(fresh, baseForPercent);

  // Extract the calculated Pips/Points distances
  const stopAmt = getEffectiveDistance(fresh, "Stop Loss");               
  const tpAmt = getEffectiveDistance(fresh, "Take Profit");

  let riskRewardRatio = "";
  if (tpAmt && stopAmt) {
    const rr = tpAmt / stopAmt;
    riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
  }

  const sym = fresh["Symbol"];                     
  const lotBasis = (Number(lots) > 0 ? Number(lots) : Number(minL));
  const units = Math.round(lotBasis * getContractSize(sym)); 
  const unitsLabel = getUnitsLabel(sym);                     

  setCalculatedSummary({
    riskRewardRatio,
    percentOfBalance: riskBudget,
    riskPercent: riskPercentDisplay,
    stopLoss: stopAmt,     // Strictly store distance in Pips
    takeProfit: tpAmt,     // Strictly store distance in Pips
    stopAmt,      
    tpAmt,
    lots,
    minLot: minL,
    totalRisk,
    targetReward,
    units,
    unitsLabel,
    pipValue: pv
  });

  setWinProjection(win);
  setLossProjection(loss);

  // Progress bar
  const initial = Number(fresh["Initial Balance"] || 0);
  const goalNum = Number(fresh["Account Goal"] || 0);
  const running = Number(fresh["Running Balance"] || 0);
  let pct = 0;
  if (goalNum > initial) pct = ((running - initial) / (goalNum - initial)) * 100;
  setProgress(Math.max(0, Math.min(100, pct)));
}


  // FIX: Prop-basis win projection with constant per‑trade target
function getWinProjectionPropBasis({
  calcStart,
  calcGoal,
  displayStart,
  riskPercent,
  riskMode,
  riskDollarFixed,
  stopLoss,
  takeProfit,
  minLot,
  maxLot,
  pipValue,
  compound = true
}) {
  const perLotRisk = Number(stopLoss) * Number(pipValue);
  if (!(perLotRisk > 0)) return { trades: [], tradeCount: 0 };

  const riskDollarFor = (bal) =>
    riskMode === "$" ? Number(riskDollarFixed || 0) : (Number(bal) * Number(riskPercent)) / 100;

  let trades = [];
  let calcBalance    = Number(calcStart)   || 0;
  let displayBalance = Number(displayStart)|| 0;

  // Base lots for non-compounding mode
  const baseBudget = riskDollarFor(calcBalance);
  let baseLots = perLotRisk > 0 ? baseBudget / perLotRisk : 0;
  baseLots = Math.max(Number(minLot), Math.min(Number(maxLot), baseLots));
  baseLots = Math.floor(baseLots / 0.01) * 0.01 || Number(minLot);

  while (calcBalance < calcGoal && trades.length < 10000) {
    const budget = compound ? riskDollarFor(calcBalance) : baseBudget;

    let lots = compound ? (budget / perLotRisk) : baseLots;
    lots = Math.max(Number(minLot), Math.min(Number(maxLot), lots));
    lots = Math.floor(lots / 0.01) * 0.01 || Number(minLot);

    const totalRisk    = lots * perLotRisk;
    const tradeProfit  = lots * Number(pipValue) * Number(takeProfit);

    trades.push({
      trade: trades.length + 1,
      balance: displayBalance,
      lots,
      riskDollar: budget,
      totalRisk,
      tradeProfit,
      nextBalance: displayBalance + tradeProfit,
      underfunded: totalRisk > budget
    });

    calcBalance    += tradeProfit;
    displayBalance += tradeProfit;
  }

  return { trades, tradeCount: trades.length };
}


// --- LOSS PROJECTION (prop basis, stops exactly at blown threshold) ---
function getLossProjectionPropBasis({
  calcStart,
  displayStart,
  riskPercent,
  riskMode,
  riskDollarFixed,
  stopLoss,
  minLot,
  maxLot,
  pipValue,
  baseBalance,
  maxLossLimit
}) {
  const perLotRisk = Number(stopLoss) * Number(pipValue);
  if (!(perLotRisk > 0)) return { trades: [], tradeCount: 0 };

  const riskDollarFor = (bal) =>
    riskMode === "$" ? Number(riskDollarFixed || 0) : (Number(bal) * Number(riskPercent)) / 100;

  const calcStartN    = Number(calcStart)    || 0;
  const displayStartN = Number(displayStart) || 0;
  const mllRaw        = Number(maxLossLimit);
  const baseN         = Number(baseBalance)  || calcStartN;

  let blownAt, displayBlownAt;
  if (Number.isFinite(mllRaw)) {
    const offset = displayStartN - calcStartN; // display = calc + offset
    blownAt        = mllRaw - offset;          // calc-track floor
    displayBlownAt = mllRaw;                   // shown in table
  } else {
    return { trades: [], tradeCount: 0, suggestedLots: 0, blownAt: NaN };
  }

  let trades = [];
  let calcBalance    = calcStartN;
  let displayBalance = displayStartN;
  let prevLots = Infinity;

  while (calcBalance > blownAt && trades.length < 10000) {
    const budget = riskDollarFor(calcBalance);
    let lots = budget > 0 ? budget / perLotRisk : 0;

    // clamp + monotonic non-increasing
    lots = Math.max(Number(minLot), Math.min(Number(maxLot), lots));
    lots = Math.min(lots, prevLots);
    lots = Math.floor(lots / 0.01) * 0.01;
    prevLots = lots;

    let lossAmount = perLotRisk * lots;
    let nextCalc = calcBalance - lossAmount;

    if (nextCalc < blownAt) {
      lossAmount = calcBalance - blownAt; // trim last loss
      nextCalc   = blownAt;
    }

    const nextDisplay = Math.max(displayBalance - lossAmount, displayBlownAt);

    trades.push({
      trade: trades.length + 1,
      balanceBefore: displayBalance,
      lots: Number(lots.toFixed(2)),
      loss: lossAmount,
      balanceAfter: nextDisplay
    });

    calcBalance    = nextCalc;
    displayBalance = nextDisplay;
  }

  return { trades, tradeCount: trades.length, blownAt };
}






const handleChecklistConfirm = async (selections) => {
  try {
    if (selections && typeof selections === "object") {
      await savePreTradeChecklist(userId, accountId, selections); // saves user-level doc now
    }
    const today = getTodayLocalISO();
    await updateUserSettings(userId, { PretradeChecklistShownDate: today }); // ✅ user-level write
    setUserPrefs(prev => ({ ...prev, PretradeChecklistShownDate: today }));  // ✅ immediate local suppress
  } catch (e) {
    console.error("[PTC] confirm error", e);
    setUserPrefs(prev => ({ ...prev, PretradeChecklistShownDate: getTodayLocalISO() })); // best-effort suppress
  } finally {
    setShowChecklist(false);
  }
};



  // --- Handlers ---
  const numericFields = [
    "Initial Balance",
    "Max Lot Size",
    "Stop Loss",
    "Daily Goal",
    "Min Lot Size",
    "Take Profit",
    "Daily Loss Limit",
    "Account Goal",
    "Running Balance",
    "Risk Percentage",
    "Leverage",
    "Pip Value",
    "Prop Initial Balance",
    "Prop Running Balance",
    "Prop Account Goal",
    "Risk Dollar"
  ];

const handleSettingChange = (key, value) => {
  // 1) Update settings (number-ify numeric fields)
  setSettings(prev => {
    // Safely handle empty strings so we don't accidentally save 0
    const next = { ...prev, [key]: numericFields.includes(key) ? (value === "" ? "" : Number(value)) : value };

    // Keep coupled fields in sync (unchanged behavior)
    if (key === "Initial Balance") next["Running Balance"] = Number(value);
    if (key === "Prop Initial Balance") next["Prop Running Balance"] = Number(value);

    // Auto-pip value on symbol change (unchanged behavior)
    if (key === "Symbol") {
      const pv = getPipValue(value, 1, (prev["Account Currency"] || next["Account Currency"]));
      next["Pip Value"] = pv;
    }

    // --- NEW: Clear values on mode switch ---
    if (key === "Stop Loss Mode") next["Stop Loss"] = "";
    if (key === "Take Profit Mode") next["Take Profit"] = "";

    return next;
  });

  // 2) If user changed the Maximum Loss Limit, mark dirty and reset; do NOT auto-calc.
  if (key === "Maximum Loss Limit" || key === "Max Loss Limit") {
    setMllDirty(true);
    resetSummaryAndProjections();
    // Do not touch popup state here; we’re just deferring calculation
    return;
  }

  // 3) For all other fields, keep your existing behavior
  setCalculatedSummary(null);
  setHasCalculated(false);
  setShowRiskWarningPopup(false);
};


  const handleJournalChange = (key, value) => setJournal(prev => ({ ...prev, [key]: value }));

  // Check daily goal popup on load
  useEffect(() => {
    if (!userId || !accountId) return;
    (async () => {
      const fresh = await getSettings(userId, accountId);
      if (fresh?.DailyGoalPopupShownDate == null) {
        await updateSettings(userId, accountId, { DailyGoalPopupShownDate: "" });
        setSettings(prev => ({ ...prev, DailyGoalPopupShownDate: "" }));
      }
    })();
  }, [userId, accountId]);

  

function runSummaryOnly(override = {}) {
  if (mllDirty) return;
  const s = { ...settings, ...override };
  // 🚫 No product selected → don't compute or show a summary
  if (!s["Symbol"]) {
    resetSummaryAndProjections();
    return;
  }

  const { Symbol, "Pip Value": pvRaw } = s;
  const pv = Number(pvRaw) || getPipValue(Symbol, 1, s["Account Currency"]);

  const calcRB = Number(s["Prop Running Balance"] ?? s["Running Balance"] ?? 0);
  const riskBudget = riskBudgetDollarForBalance(s, calcRB);
  const riskPercentDisplay = riskPercentFromBudget(s, calcRB);

  // The engine naturally converts Price to Pips using getEffectiveDistance internally
  const { win, loss, lots, totalRisk, targetReward } = computeProjections({ ...s, "Pip Value": pv });

  // Extract the calculated Pips/Points distances for the Summary Table
  const stopAmt = getEffectiveDistance(s, "Stop Loss");
  const tpAmt = getEffectiveDistance(s, "Take Profit");

  let riskRewardRatio = "";
  if (tpAmt && stopAmt) {
    const rr = tpAmt / stopAmt;
    riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
  }

  const lotBasis = (Number(lots) > 0 ? Number(lots) : Number(s["Min Lot Size"] || 0.01));
  const units = Math.round(lotBasis * getContractSize(Symbol));
  const unitsLabel = getUnitsLabel(Symbol);

  setCalculatedSummary({
    riskRewardRatio,
    percentOfBalance: riskBudget,     
    riskPercent: riskPercentDisplay,  
    stopLoss: stopAmt,                  // Strictly store distance in Pips
    takeProfit: tpAmt,                  // Strictly store distance in Pips
    stopAmt,      
    tpAmt,              
    lots,
    minLot: Number(s["Min Lot Size"] || 0.01),
    totalRisk,
    targetReward,
    units,
    unitsLabel,
    pipValue: pv
  });

  setJournal(prev => ({ ...prev, Size: lots }));
  setWinProjection(win);
  setLossProjection(loss);
  setShowRiskWarningPopup(false);
 }

// Recompute projections whenever Maximum Loss Limit changes (no popups)
useEffect(() => {
  if (!loadedFromServer.current) return;
  if (!settings["Symbol"]) return; // 🚫 don't auto-calc without a product
  if (typeof settings["Maximum Loss Limit"] === "undefined") return;

  runSummaryOnly({ "Maximum Loss Limit": Number(settings["Maximum Loss Limit"] || 0) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [settings && settings["Maximum Loss Limit"]]);



// Replace the existing checkDrawdown with this edge‑triggered + lock/unlock version
async function checkDrawdown(
  trades,
  drawdownLimitPct,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  setDrawdownAmount,
  setDrawdownPct
) {
  try {
    if (
      !Array.isArray(trades) ||
      !Number.isFinite(Number(drawdownLimitPct)) ||
      Number(drawdownLimitPct) <= 0
    ) {
      return;
    }

    const todayISO  = getTodayLocalISO();
    const POPUP_ID  = "drawdownWarning";
    const SHOWN_KEY = "DrawdownPopupShownDate"; // kept for backward compatibility / migration

    // ---- helpers -----------------------------------------------------------
    const toNumber = (v) => {
      if (typeof v === "number") return v;
      if (v === null || typeof v === "undefined") return NaN;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
      try { return parseCurrency(v); } catch { return NaN; }
    };

    const rowTimestamp = (row) => {
      const d = row?.["Date"];
      let base;
      if (!d) base = new Date(0);
      else if (typeof d === "string") {
        if (d.includes("-")) base = new Date(d); // "YYYY-MM-DD" or ISO
        else if (d.includes("/")) {
          const [mm, dd, yyyy] = d.split("/").map(Number);
          base = new Date(yyyy || 1970, (mm || 1) - 1, dd || 1);
        } else base = new Date(d);
      } else if (d?.seconds) base = new Date(d.seconds * 1000);
      else base = new Date(d);

      let secs = 0;
      const t = row?.["Exit Time"] || row?.["Entry Time"] || "";
      if (t) {
        try { secs = timeStringToSeconds(t); } catch { secs = 0; }
      }
      return base.getTime() + (Number.isFinite(secs) ? secs * 1000 : 0);
    };

    const getBalance = (row) => {
      const rb = toNumber(row?.["Running Balance"]);
      if (Number.isFinite(rb) && rb > 0) return rb;
      const ab = toNumber(row?.["Actual Balance"]);
      if (Number.isFinite(ab) && ab > 0) return ab;
      return NaN;
    };
    // -----------------------------------------------------------------------

    // Deterministic sort
    const rowsSorted = [...trades].sort((a, b) => {
      const na = Number(a?.tradeNum ?? a?.["Trade #"]);
      const nb = Number(b?.tradeNum ?? b?.["Trade #"]);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return rowTimestamp(a) - rowTimestamp(b);
    });

    // Equity series (positive numbers only)
    const series = rowsSorted
      .map(getBalance)
      .filter((n) => Number.isFinite(n) && n > 0);

    // Include a configured Initial Balance as a potential historical peak
    const initial = toNumber(currentSettings?.["Initial Balance"]);
    if (Number.isFinite(initial) && initial > 0) series.push(initial);

    if (series.length === 0) return;

    // Last positive balance
    let last = NaN;
    for (let i = rowsSorted.length - 1; i >= 0; i--) {
      const b = getBalance(rowsSorted[i]);
      if (Number.isFinite(b) && b > 0) { last = b; break; }
    }
    if (!Number.isFinite(last) || last <= 0) return;

    const peak = Math.max(...series); // historical max
    if (!(peak > 0)) return;

    const ddAmount = +(peak - last).toFixed(2);
    const ddPct = Math.min(100, Math.max(0, ((peak - last) / peak) * 100));

    // keep UI state in sync
    if (typeof setDrawdownAmount === "function") setDrawdownAmount(ddAmount);
    if (typeof setDrawdownPct === "function")    setDrawdownPct(ddPct);

    // --- lock/unlock state pulled from settings
    const lockActive   = Boolean(currentSettings?.DrawdownLockActive);
    const lockPeak     = Number(currentSettings?.DrawdownLockPeak || 0);
    const lockStarted  = String(currentSettings?.DrawdownLockStartedDate || "");
    const shownDateRaw = String(currentSettings?.[SHOWN_KEY] || "");
    const overLimit    = ddPct >= Number(drawdownLimitPct);

    // --- MIGRATION: if a previous daily-gated show happened (shown date present)
    // and we are still over limit but no lock yet, silently adopt into the new lock
    if (overLimit && !lockActive && shownDateRaw) {
      const patch = {
        DrawdownLockActive: true,
        DrawdownLockPeak: peak,
        DrawdownLockStartedDate: shownDateRaw,
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(prev => ({ ...prev, ...patch }));
      return; // do not re-show
    }

    // --- EDGE-TRIGGERED LOGIC ---
    if (overLimit) {
      if (!lockActive) {
        // crossing from below → above: fire once and lock
        queuePopup(POPUP_ID, {
          drawdownLimitPct: Number(drawdownLimitPct),
          drawdownAmount: ddAmount,
          drawdownPct: ddPct,
        });

        const patch = {
          [SHOWN_KEY]: todayISO,          // optional: keep for analytics/visibility
          DrawdownLockActive: true,
          DrawdownLockPeak: peak,
          DrawdownLockStartedDate: todayISO,
        };
        await updateSettingsFunc(userId, accountId, patch);
        setLocalSettings?.(prev => ({ ...prev, ...patch }));
      }
      // if locked and still over the limit → do nothing (no re-trigger)
      return;
    }

    // below the limit → unlock (and clear old daily gate if present)
    if (lockActive || shownDateRaw) {
      const patch = {
        [SHOWN_KEY]: "",
        DrawdownLockActive: false,
        DrawdownLockPeak: 0,
        DrawdownLockStartedDate: "",
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(prev => ({ ...prev, ...patch }));
    }
  } catch (e) {
    console.error("checkDrawdown error:", e);
  }
}


// --- Risk helpers (budget in dollars and display percent) ---
function getRiskMode(s) {
  return String(s?.["Risk Mode"] || "%") === "$" ? "$" : "%";
}
function riskBudgetDollarForBalance(s, balanceMaybe) {
  const mode = getRiskMode(s);
  const base = Number(balanceMaybe ?? s?.["Prop Running Balance"] ?? s?.["Running Balance"] ?? 0);
  if (mode === "$") return Math.max(0, Number(s?.["Risk Dollar"] || 0));
  return Math.max(0, base * Number(s?.["Risk Percentage"] || 0) / 100);
}
function riskPercentFromBudget(s, balanceMaybe) {
  const base = Number(balanceMaybe ?? s?.["Prop Running Balance"] ?? s?.["Running Balance"] ?? 0);
  if (base <= 0) return 0;
  return (riskBudgetDollarForBalance(s, base) / base) * 100;
}



// Bold + color any literal "100%" in a line of text
function highlightHundredPercent(line) {
  if (!line) return null;
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">
        100%
      </span>
    ) : (
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}



async function checkMaxLossLimitBlown(
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();
  const shownKey = "MaxLossLimitBlownShownDate";

  // Ledger balance is what prop firms check against their floor
  const runningBalance = Number(currentSettings["Running Balance"] ?? 0);
  const initialBalance = Number(currentSettings["Initial Balance"] ?? 0);

  // Prefer an explicitly saved floor (firms may move it upward)
  const limitFloorRaw = Number(
    currentSettings["Maximum Loss Limit"] ??
    currentSettings["Max Loss Limit"] ??
    0
  );

  // Fallback if only an amount was recorded at account creation
  const lossAmount = Number(
    currentSettings["Maximum Loss Amount"] ??
    currentSettings["Max Loss Amount"] ??
    currentSettings["Maximum Loss"] ??
    0
  );


    // Decide the floor to use — it can be negative or positive
    let limitFloor = NaN;
    if (Number.isFinite(limitFloorRaw)) {
      // Treat "Maximum Loss Limit" as an absolute balance floor (prop firms may report negative)
      limitFloor = limitFloorRaw;
    } else if (Number.isFinite(initialBalance) && Number.isFinite(lossAmount)) {
      // Legacy fallback when only a loss amount is stored
      limitFloor = initialBalance - Math.abs(lossAmount);
    }
  
    // If we still don't have a valid floor or balance, do nothing
    if (!Number.isFinite(limitFloor) || !Number.isFinite(runningBalance)) return;
   

  const blownNow = Number(runningBalance) <= Number(limitFloor);

  
  if (blownNow && !currentSettings[shownKey]) {
    // Provide both key styles so either modal variant can render
    queuePopup("maxLossLimitBlown", {
      base: initialBalance,
      maxLoss: limitFloor,     // legacy modal expects 'maxLoss'
      rb: runningBalance,
      initial: initialBalance, // alt modal expects 'initial'
      limit: limitFloor        // alt modal expects 'limit'
    });

    const patch = { [shownKey]: today };
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings(prev => ({ ...prev, ...patch }));
  }
}



const handleCloseSaveConfirmation = async () => {
   setShowTradeSaved(false); // Hide the "Trade Saved" popup

   if (postSaveCheckInfo) {
     popupsAllowedRef.current = true;
     // ✅ Add the last two arguments to this function call
     await runAllPopupChecks(
       postSaveCheckInfo.rows,
       postSaveCheckInfo.settings,
       postSaveCheckInfo.projection,
       postSaveCheckInfo.startBal,
       postSaveCheckInfo.pnl
     );
     popupsAllowedRef.current = false;
     setPostSaveCheckInfo(null);
   }
 };

    const isTrueLoss = (val, pnl) => {
    // Check for boolean true or the string "true"
    if (val === true || String(val).toLowerCase() === 'true') {
      return true;
    }
    // As a fallback, if PnL (profit/loss) is available and negative, it's a loss
    if (pnl != null && !isNaN(Number(pnl))) {
      return Number(pnl) < 0;
    }
    return false;
  };



  // This function contains all the popup logic moved from handleSaveTrade
    async function runAllPopupChecks(allRows, currentSettings, lossProjection, startBal, pnl) {
      const today = getTodayLocalISO();
      let settingsForChecks = { ...currentSettings };

      // ✅ Daily loss limit
      await checkDailyLossLimit(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);

     // ✅ Daily goal popup (run after a save so the popup can actually show)
     await checkDailyProfitTarget(
       allRows,
       Number(settingsForChecks["Daily Goal"] || 0),
       settingsForChecks,
       updateSettings,
       userId,
       accountId,
       setSettings
     );


    // -------------------------------
    // Over Risk Warning (Prop logic)
    // -------------------------------
    const startPropBal = Number(settingsForChecks["Prop Running Balance"] || 0) - (Number(pnl) || 0);
    const lossForTrade = Math.max(0, -(Number(pnl) || 0));
    const allowedRisk  = riskBudgetDollarForBalance(settingsForChecks, startPropBal);


    if (
      lossForTrade > 0 &&
      allowedRisk > 0 &&
      lossForTrade > allowedRisk &&
      settingsForChecks.OverRiskWarningShownDate !== today
    ) {
      // Pass the exact keys your prop modal renders
      queuePopup('overRiskWarning', { lossForTrade, allowedRisk });
      const update = { OverRiskWarningShownDate: today };
      await updateSettings(userId, accountId, update);  
      settingsForChecks = { ...settingsForChecks, ...update };
    }

    // -------------------------------
    // Prop firm rule checks (unchanged)
    // -------------------------------
    await checkMaxLossLimitBlown(settingsForChecks, updateSettings, userId, accountId, setSettings);
    await checkAccountGoalReached(settingsForChecks, updateSettings, userId, accountId, setSettings);

    // ---------------------------------------------------
    // Helper: will "twoLossAfterGoalWarning" fire now?
    // ---------------------------------------------------
    const twoLossAfterGoalWillTrigger = (() => {
      const dailyGoal = Number(settingsForChecks["Daily Goal"] || 0);
      if (!dailyGoal) return false;

      const todayISO   = getTodayLocalISO();
      const todayTrades = (allRows || []).filter(t => (t["Date"] || "").startsWith(todayISO));
      if (todayTrades.length < 3) return false; // need at least goal hit + 2 more trades

      // Find first time the cumulative P&L hits/exceeds goal
      let running = 0;
      let hitIdx  = -1;
      for (let i = 0; i < todayTrades.length; i++) {
        const r = Number(todayTrades[i]["Actual Reward"] || 0);
        running += Number.isFinite(r) ? r : 0;
        if (running >= dailyGoal) { hitIdx = i; break; }
      }
      if (hitIdx === -1) return false;

      const after = todayTrades.slice(hitIdx + 1);
      if (after.length < 2) return false;

      // Use your robust "loss" detector from this file
      return after.slice(0, 2).every(t => isTrueLoss(t["Loss"], t["Actual Reward"]));
    })();

    // -----------------------------------------
    // Inline checks for 2 and 3 consecutive losses
    // -----------------------------------------
    const lastThree   = allRows.slice(-3);
    const lastTwo     = allRows.slice(-2);
    const lostAllThree = lastThree.length === 3 &&
      lastThree.every(t => isTrueLoss(t["Loss"], t["Actual Reward"]));
    const lostBoth     = lastTwo.length === 2 &&
      lastTwo.every(t => isTrueLoss(t["Loss"], t["Actual Reward"]));

    // 3 Loss Warning (unchanged)
    if (lostAllThree && settingsForChecks.ThreeLossWarningShownDate !== today) {
      const message =
        "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\n Trading is 80% Trading Psychology. This may be a good time to watch some psychology videos.  The video below from Psychology of Profits is a good one to start with.";
      queuePopup("threeLossWarning", { message, videoLink: "https://www.youtube.com/watch?v=s-_oWdFkvkw" });
      const update = { ThreeLossWarningShownDate: today };
       await updateSettings(userId, accountId, update);     // patch only the flag
       settingsForChecks = { ...settingsForChecks, ...update };
       setSettings(prev => ({ ...prev, ...update }));       
    }

    // 2 Loss Warning — now **suppressed** if "twoLossAfterGoal" will also trigger
    if (
      lostBoth &&
      !lostAllThree &&
      !twoLossAfterGoalWillTrigger &&           // <-- NEW guard
      settingsForChecks.TwoLossWarningShownDate !== today
    ) {
      const message = TWO_LOSS_MESSAGE;
      queuePopup("twoLossWarning", { message });
      const update = { TwoLossWarningShownDate: today };
      await updateSettings(userId, accountId, update);
      settingsForChecks = { ...settingsForChecks, ...update };
      setSettings(prev => ({ ...prev, ...update }));
    }

    // -----------------------------------------
    // The rest of your existing checks (unchanged)
    // -----------------------------------------
    await checkTwoLossAfterGoal(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings, queuePopup);
    await checkConsecutiveWins(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);
    await checkMoodVolatility(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);
    await checkLessonsLearnedStreak(allRows, settingsForChecks, updateSettings, userId, accountId, setSettings);
    await checkRiskOfRuin(lossProjection, settingsForChecks, updateSettings, userId, accountId, setSettings);

    // Gated checks
    if (settingsForChecks["Max Trades Per Day Toggle"]) {
      await checkOvertrading(
        allRows,
        Number(settingsForChecks["Max Trades Per Day"] || 10),
        settingsForChecks,
        updateSettings,
        userId,
        accountId,
        setSettings
      );
    }
    if (settingsForChecks["Max Loss Trades Per Day Toggle"]) {
      await checkTooManyLosses(
        allRows,
        Number(settingsForChecks["Max Loss Trades Per Day"] || 5),
        settingsForChecks,
        updateSettings,
        userId,
        accountId,
        setSettings
      );
    }

    await checkDrawdown(
      allRows,
      Number(settingsForChecks["Drawdown Limit %"] || 0),
      settingsForChecks,
      updateSettings,
      userId,
      accountId,
      setSettings
    );

    // Single-trade checks
    const latestTrade = allRows.length ? allRows[allRows.length - 1] : null;
    if (latestTrade) {
      await checkFirstTradeAnalysis(latestTrade, userId, queuePopup);
      await checkWin10NoRulesPopup(latestTrade, settingsForChecks, updateSettings, userId, accountId, setSettings, queuePopup);
      await checkWin10WithRulesPopup(latestTrade, settingsForChecks, updateSettings, userId, accountId, setSettings, queuePopup);
    }

    // Sync any updated flags
    setSettings(prev => ({ ...settingsForChecks, ...prev })); // prev wins on conflicts
  }


    async function handleSaveTrade() {
    const errs = [];



    if (!journal["Actual Balance"]) errs.push("- Actual Balance");
    if (!journal["Date"]) errs.push("- Date");
    if (!settings["Symbol"]) errs.push("- Symbol");
    if (!journal["Did I follow my Rules?"]) errs.push("- Did I follow my Rules?");
    if (!journal["Win"] && !journal["Loss"]) errs.push("- Win/Loss");
    if (!calculatedSummary) errs.push("- Click Calculate before saving");
    if (errs.length) {
      setMissingFields(errs);
      return;
    }

    setSavingTrade(true);
    try {
      const startRB = Number(settings["Running Balance"]) || 0;
      const endRB = Number(journal["Actual Balance"]) || 0;
      const delta = endRB - startRB;

      // ✅ FIX: Get the starting Prop Balance before the trade
      const prevPropRB = Number(settings["Prop Running Balance"] ?? startRB);
      const nextPropRB = prevPropRB + delta;
      
      const currentSettings = {
        ...settings,
        "Running Balance": endRB,
        "Prop Running Balance": nextPropRB,
      };

      // Auto‑compute Pips for journaling if left blank
      let pipsForSave = journal["Pips"];
      const pipsProvided =
        pipsForSave != null && String(pipsForSave).trim() !== "" && Number.isFinite(Number(pipsForSave));

      if (!pipsProvided) {
        const derivedAbs = computePipsFromPricesAbs(
          settings["Symbol"],
          journal["Entry Price"],
          journal["Exit Price"]
        );
        if (derivedAbs != null && Number.isFinite(derivedAbs)) {
          pipsForSave = derivedAbs;
        }
      }

      let signedPipsForSave = null;
      if (pipsForSave != null && String(pipsForSave).trim() !== "" && Number.isFinite(Number(pipsForSave))) {
        const n = Number(pipsForSave);
        const abs = Math.abs(n);
        signedPipsForSave = journal["Loss"] ? -abs : abs;
      }


      const toSave = { ...journal }
      if (signedPipsForSave != null) {
        toSave["Pips"] = signedPipsForSave;
        toSave["Ticks"] = signedPipsForSave * 10;
      }

      const sizeSource =
        (journal["Size"] !== undefined && journal["Size"] !== "")
          ? journal["Size"]
          : (calculatedSummary?.lots ?? "");
      const effectiveLots = Number(sizeSource);

      // store clean Size (two decimals) once
      toSave["Size"] = Number.isFinite(effectiveLots) ? Number(effectiveLots.toFixed(2)) : "";

      // persist SL/TP (individual + combined)
      toSave["Stop Loss"] = settings["Stop Loss"] || "";
      toSave["Take Profit"] = settings["Take Profit"] || "";
      toSave["Stop Loss_Take Profit"] =
        settings["Stop Loss"] && settings["Take Profit"]
          ? `${settings["Stop Loss"]}/${settings["Take Profit"]}`
          : "";

      // ✅ compute risk FIRST, then use it
      const slPips   = Number(calculatedSummary?.stopAmt ?? settings["Stop Loss"] ?? 0);
      const pipValue = Number(settings["Pip Value"]) ||
                       getPipValue(settings["Product"], 1, settings["Account Currency"]);
      const actualRiskDollar   = Math.max(0, (toSave["Size"] || 0) * slPips * pipValue);
      const actualRewardDollar = (Number(journal["Actual Balance"]) || 0) - (Number(settings["Running Balance"]) || 0);

      // now safe to save risk fields
      toSave["Total Risk"] = Number(actualRiskDollar.toFixed(2));
      toSave["Risk Amount"] = toSave["Total Risk"]; // optional alias
      // ✅ Save Percent of Balance as a DOLLAR budget (Prop RB × Risk%)
      {
        const baseForPercent = Number(
          settings["Prop Running Balance"] ?? settings["Running Balance"] ?? 0
        );
        const rp = Number(settings["Risk Percentage"] || 0);
        const pob = calculatedSummary?.percentOfBalance ?? (baseForPercent * rp / 100);
        toSave["Percent of Balance"] = Number((pob || 0).toFixed(2));
      }


       // ✅ Percent of Balance (trade risk as % of starting *Prop* balance)
/*       toSave["Percent of Balance"] =
         Number(prevPropRB) > 0 ? Number(((actualRiskDollar / Number(prevPropRB)) * 100).toFixed(2)) : 0;*/

      // Risk/Reward string
      let rrStr = "";
       if (actualRiskDollar > 0) {
         const sign = actualRewardDollar < 0 ? "-" : "";
         const r = Math.abs(actualRewardDollar) / actualRiskDollar;
         const rStr = Number.isInteger(r) ? `${r}` : r.toFixed(2).replace(/\.?0+$/, "");
         rrStr = `${sign}${rStr}:1`;
       }
      toSave["Actual Risk to Reward"] = rrStr;

      // rest of your fields...
      toSave["Product"]   = settings["Symbol"] || "";
      toSave["Duration"]  = getDurationHHMMSS(to24HourTime(journal["Entry Time"]), to24HourTime(journal["Exit Time"]));
      toSave["Trade #"]   = await getNextTradeNumber(userId, accountId) || "";
      toSave["Actual Reward"] = actualRewardDollar.toFixed(2);
      if (!toSave["Date"]) toSave["Date"] = getTodayLocalISO();
      toSave["tickSize"] = getPipSizeForSymbol(settings["Product"]); 
      toSave["assetClass"] = "FOREX"
      toSave["Potential"] = journal["Potential"] || "";
      // ✅ FIX: Calculate percent growth based on the Prop Balance
      if (prevPropRB > 0) {
        toSave["Actual Percent Growth"] = ((actualRewardDollar / prevPropRB) * 100).toFixed(2) + "%";
      }

      if (!toSave["Date"]) toSave["Date"] = getTodayLocalISO();

      await appendTrade(userId, accountId, toSave);
      toSave["LogLesson"] = !!journal.LogLesson;
      if (journal.LogLesson && (journal.Lesson || "").trim()) {
        try {
          const textOnly = (journal.Lesson || "").trim();
          await appendLesson(textOnly);   // ✅ single argument only
          // IMPORTANT: Do not use the return value; do not write it back to trade fields.
        } catch (err) {
          console.error("Error appending lesson from CalculateForm:", err);
        }
      }
       await updateSettings(userId, accountId, {
         "Running Balance": endRB,
         "Prop Running Balance": nextPropRB,
       });
       setSettings(currentSettings);

       // === Copy Trade (if this account is a Forex leader) ===
       try {
         // delta is your per‑trade P/L in dollars: +(endRB - startRB).toFixed(2)
         const me = await getSettings(userId, accountId);
         if (me?.CopyTrade?.role === "leader" || me?.settings?.CopyTrade?.role === "leader") {
           const { copyTradeToFollowers } = await import("../services/CopyTradeService");
           await copyTradeToFollowers({
             userId,
             leaderAccountId: accountId,
             leaderTrade: toSave,       // the trade you just saved
             delta,                     // number (can be +/-)
             accountType: "forex"       // enforce FX → FX only
           });
         }
       } catch (e) {
         console.error("[CopyTrade] forex prop copy failed", e);
       }



      await refreshAfterSave(userId, accountId); // NEW: recompute projections from saved balances

      const rowsAfter = await fetchJournalRows(userId, accountId);
      const rowsWithThisTrade = rowsAfter.some(r => String(r["Trade #"]) === String(toSave["Trade #"]))
        ? rowsAfter
        : [...rowsAfter, toSave];
      
      await updateDailyProgress(rowsWithThisTrade);
      
      

      setPostSaveCheckInfo({
        rows: rowsWithThisTrade,
        settings: currentSettings,
        projection: lossProjection,
        startBal: startRB,
        pnl: delta
      });

      const defTf = String(settings?.["Default Timeframe"] || "").trim();

      setJournal({
        ...defaultJournal,
        Size: calculatedSummary?.lots ?? "",
        Timeframe: defTf, // ✅ keep default after save
        Session: String(settings?.["Default Session"] || "").trim(),
         "Entry Strategy": String(settings?.["Default Entry Strategy"] || "").trim(),
         "Exit Strategy": String(settings?.["Default Exit Strategy"] || "").trim(),
         "Emotional Notes": "",
      });
      setShowTradeSaved(true);

    } catch (err) {
      alert("Failed to save journal entry.");
      console.error("handleSaveTrade error:", err);
    } finally {
      setSavingTrade(false);
    }
  }



   // Remove ephemeral popup flags before persisting bulk settings
   function sanitizeSettingsForPersist(s) {
     const copy = { ...s };
     for (const k of Object.keys(copy)) {
       if (/ShownDate$/.test(k) || k === "DailyGoalPopupShownForGoal") {
         delete copy[k];
       }
     }
     return copy;
   }

   // === Risk Toggle Helpers ===
   function getRiskMode(s) {
     return String(s?.["Risk Mode"] || "%") === "$" ? "$" : "%";
   }
   function riskBudgetDollar(s, balanceMaybe) {
     const mode = getRiskMode(s);
     const bal = Number(balanceMaybe ?? s?.["Prop Running Balance"] ?? s?.["Running Balance"] ?? 0);
     if (mode === "$") return Math.max(0, Number(s?.["Risk Dollar"] || 0));
     return Math.max(0, bal * Number(s?.["Risk Percentage"] || 0) / 100);
   }
   function riskPercentFromDollar(s, balanceMaybe) {
     const bal = Number(balanceMaybe ?? s?.["Prop Running Balance"] ?? s?.["Running Balance"] ?? 0);
     const dollar = riskBudgetDollar(s, bal);
     if (bal <= 0) return 0;
     return (dollar / bal) * 100;
   }


  async function handleCalculate(isManualClick = true) {
    if (isManualClick) setSavingSettings(true);
    setHasCalculated(true);
    let freshSettings = { ...settings };
    const today = getTodayLocalISO();
    try {
      await updateSettings(userId, accountId, sanitizeSettingsForPersist(settings));
      freshSettings = await getSettings(userId, accountId);

      const {
        Symbol,
        "Min Lot Size": minL,
        "Pip Value": pvRaw
      } = freshSettings;

      const pv = Number(pvRaw) || getPipValue(Symbol, 1, settings["Account Currency"]);
      
      const baseBalance = Number(freshSettings["Prop Running Balance"] ?? freshSettings["Running Balance"] ?? 0);
      const riskBudget = riskBudgetDollarForBalance(freshSettings, baseBalance);
      const riskPercentDisplay = riskPercentFromBudget(freshSettings, baseBalance);

      // Extract the calculated Pips/Points distances
      const stopAmt = getEffectiveDistance(freshSettings, "Stop Loss");
      const tpAmt = getEffectiveDistance(freshSettings, "Take Profit");

      let riskRewardRatio = "";
      if (tpAmt && stopAmt) {
        const rr = tpAmt / stopAmt;
        riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
      }

      // Projections
      const { win, loss, lots, totalRisk, targetReward } = computeProjections(freshSettings);

      const lotBasis = (Number(lots) > 0 ? Number(lots) : Number(minL || 0.01));
      const units = Math.round(lotBasis * getContractSize(Symbol));
      const unitsLabel = getUnitsLabel(Symbol);

      setWinProjection(win);
      setLossProjection(loss);

      setCalculatedSummary({
        riskRewardRatio,
        percentOfBalance: riskBudget,        
        riskPercent: riskPercentDisplay,     
        stopLoss: stopAmt,    // Strictly store distance in Pips
        takeProfit: tpAmt,    // Strictly store distance in Pips
        stopAmt,      
        tpAmt,
        lots,
        minLot: Number(minL || 0.01),
        totalRisk,
        targetReward,
        units,
        unitsLabel,
        pipValue: pv
      });

      setMllDirty(false);

      if (isManualClick) {
        if (Number(totalRisk) > Number(riskBudget)) setShowRiskWarningPopup(true);
        else setShowRiskWarningPopup(false);
      }
    } catch (err) {
      alert("Failed to calculate/save settings");
      console.error("Settings save failed:", err);
    }
    if (isManualClick) setSavingSettings(false);
  }


  // Load settings on mount
  useEffect(() => {
    const load = async () => {
      try {
        hasRunInitialAutoCalc.current = false; // reset for a new user/account
        const settingsObj = await getSettings(userId, accountId);
        setSettings({ ...defaultSettings, ...settingsObj });
        loadedFromServer.current = true;       // initial fetch completed
        // --- ADD THIS LOGIC ---
                // Fetch journal rows to extract past strategies
                const journalRows = await fetchJournalRows(userId, accountId);
                if (journalRows) {
                  // Use a Set to get unique, non-empty values, then convert back to an array
                  const uniqueEntryStrategies = [
                    ...new Set(journalRows.map(row => row["Entry Strategy"]).filter(Boolean))
                  ];
                  const uniqueExitStrategies = [
                    ...new Set(journalRows.map(row => row["Exit Strategy"]).filter(Boolean))
                  ];
                  
                  setEntryStrategies(uniqueEntryStrategies);
                  setExitStrategies(uniqueExitStrategies);
                }
                // --- END OF ADDED LOGIC ---
      } catch (err) {
        console.error("Error loading settings:", err);
      }
    };
    load();
  }, [userId, accountId]);

  // Progress bar % to goal
  useEffect(() => {
    const initial = Number(settings["Initial Balance"] || 0);
    const goal = Number(settings["Account Goal"] || 0);
    const running = Number(settings["Running Balance"] || 0);
    let pct = 0;
    if (goal > initial) pct = ((running - initial) / (goal - initial)) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }, [settings["Initial Balance"], settings["Account Goal"], settings["Running Balance"]]);


  // Render helpers
  // Safely normalize the suggested lot size for display + math
  const suggestedLotsNum = Number(calculatedSummary?.lots);
  const suggestedLots    = Number.isFinite(suggestedLotsNum) && suggestedLotsNum > 0
    ? suggestedLotsNum
    : 0;

  const isZeroLot = suggestedLots === 0;

  // Use suggestedLots (or minLot) wherever a lot basis is needed for math
  const lotBasisForStop  = suggestedLots > 0 ? suggestedLots : minLot;
  const maxStopLossValue = calculatedSummary
    ? Math.floor(
        Number(calculatedSummary.percentOfBalance || 0) /
        (lotBasisForStop * Number(pipValue || 0))
      )
    : 0;

  const isTightStop = maxStopLossValue > 0 && maxStopLossValue < 5;


 // Projections calc helpers
 // FIX: Win projection with a constant per‑trade target (no compounding)
 function getCompoundingProjection({
   displayBalance,   // shows in table (from Running Balance)
   displayGoal,      // shows in table (from Account Goal)
   calcBalance,      // used for sizing math (from Prop Running Balance)
   calcGoal,         // used for sizing math (from Prop Account Goal)
   riskPercent,
   stopLoss,
   takeProfit,
   maxLot,
   minLot,
   pipValue
 }) {
   const trades = [];                                  // <-- declare it
   let dBal = Number(displayBalance) || 0;
   let cBal = Number(calcBalance) || 0;
   const cGoalNum = Number(calcGoal) || 0;

   const perLotRisk = Number(stopLoss) * Number(pipValue || 0);
   if (!(perLotRisk > 0)) return { trades: [], tradeCount: 0 };

   // Compute the starting lot size once (based on the starting calc balance)
   const startRiskDollar = (cBal * Number(riskPercent)) / 100;
   let baseLots = perLotRisk > 0 ? startRiskDollar / perLotRisk : 0;
   baseLots = Math.max(Number(minLot), Math.min(Number(maxLot), baseLots));
   baseLots = Math.floor(baseLots / 0.01) * 0.01;
   if (baseLots <= 0) baseLots = Number(minLot);

   // Fixed per-trade risk and profit (constant for all rows)
   const perTradeRiskDollar = baseLots * perLotRisk;
   const perTradeProfit     = baseLots * Number(pipValue) * Number(takeProfit);
   if (!(perTradeProfit > 0)) return { trades: [], tradeCount: 0 };

   let tradeCount = 0;
   while (cBal < cGoalNum && tradeCount < 10000) {
     trades.push({
       trade: tradeCount + 1,
       balance: dBal,                         // display track
       lots: baseLots,                        // fixed
       riskDollar: perTradeRiskDollar,        // fixed
       tradeProfit: perTradeProfit,           // fixed
       nextBalance: dBal + perTradeProfit,
       // underfunded if the fixed risk exceeds allowed risk for this row
       underfunded: perTradeRiskDollar > ((cBal * Number(riskPercent)) / 100)
     });

     // advance both tracks with the fixed per-trade profit
     cBal += perTradeProfit;                  // prop engine
     dBal += perTradeProfit;                  // display
     tradeCount++;
   }

   return { trades, tradeCount: trades.length };
 }



 function getLossProjection({
   displayBalance,   // shows in table (from Running Balance)
   calcBalance,      // used for sizing math (from Prop Running Balance)
   stopLoss,
   pipValue,
   minLot,
   maxLot,
   riskPercent
 }) {
   const trades = [];                                  // <-- declare it
   let dBal = Number(displayBalance) || 0;
   let cBal = Number(calcBalance) || 0;
   let tradeCount = 0;

   const perLotRisk = Number(stopLoss) * Number(pipValue || 0);
   if (perLotRisk <= 0) return { trades: [], tradeCount: 0, suggestedLots: 0 };

   let prevLots = Infinity; // enforce non-increasing lots

   while (cBal > 0 && tradeCount < 1000) {
     const riskDollar = (Number(riskPercent) / 100) * cBal;
     let lots = riskDollar > 0 ? riskDollar / perLotRisk : 0;
     lots = Math.floor(lots / 0.01) * 0.01;
     lots = Math.max(Number(minLot), Math.min(Number(maxLot), lots));

     // Guarantee monotonic non-increasing lots as balance decreases:
     if (lots > prevLots) lots = prevLots;
     prevLots = lots;

     const lossPerTrade = perLotRisk * lots;
     if (lossPerTrade <= 0) break;

     tradeCount += 1;

     trades.push({
       trade: tradeCount,
       balanceBefore: dBal,                        // show on display track
       lots: Number(lots.toFixed(2)),              // based on prop calc
       loss: lossPerTrade,                         // based on prop calc
       balanceAfter: Math.max(dBal - lossPerTrade, 0) // show on display track
     });

     // advance both tracks
     cBal = Math.max(cBal - lossPerTrade, 0);      // prop calc engine
     dBal = Math.max(dBal - lossPerTrade, 0);      // display track
   }

   return { trades, tradeCount: trades.length, suggestedLots: 0 };
 }




 const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");


 function formatThreeLossMessage(line) {
   if (!line) return null;

   const highlights = {
     "Take a break!": "font-bold text-red-500 dark:text-red-500",
     "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
   };

   // Escape phrases so regex treats them literally
   const escaped = Object.keys(highlights).map(escapeRegExp).join("|");
   const regex = new RegExp(`(${escaped})`, "g");

   return line.split(regex).map((part, i) => {
     return highlights[part]
       ? <span key={i} className={highlights[part]}>{part}</span>
       // ⬇️ route anything else through the tilt tooltip wrapper
       : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>;
   });
 }

 // 🔁 Always refresh progress bars whenever balances or trades change
 useEffect(() => {
   if (!userId || !accountId) return;

   // Re-run daily PnL calculation
   updateDailyProgress();

   console.log("[DailyProgress] Refreshed due to balance or trade changes");
 }, [
   userId,
   accountId,
   settings["Running Balance"],
   calculatedSummary,
 ]);

 // --- UI LOGIC FOR SL/TP MODES ---
   const isIdx = isIndexSymbol(settings["Symbol"]);
   const fxUnit = isIdx ? "Points" : "Pips";

   // Entry is required if either exit is set to 'Price' and has a value
   const entryRequired = (settings["Stop Loss Mode"] === "Price" && Number(settings["Stop Loss"]) > 0) || 
                         (settings["Take Profit Mode"] === "Price" && Number(settings["Take Profit"]) > 0);
   
   const showEntryWarning = entryRequired && !settings["Entry Price"];

const canCalculate = useMemo(() => {
      // 1. Symbol is always required
      if (!settings["Symbol"]) return false;

      // 2. Risk Amount/Percentage must be > 0
      const riskVal = settings["Risk Mode"] === "$" 
        ? Number(settings["Risk Dollar"] ?? settings["Risk Amount"] ?? 0) 
        : Number(settings["Risk Percentage"]);
      if (!riskVal || riskVal <= 0) return false;

      // 3. Stop Loss must be present and > 0
      const stopLoss = Number(settings["Stop Loss"]);
      if (isNaN(stopLoss) || stopLoss <= 0) return false;

      // 4. Asset-specific or Mode-specific requirements
      if (
        settings["Stop Loss Mode"] === "Price" || 
        settings["Take Profit Mode"] === "Price"
      ) {
        const entryPrice = Number(settings["Entry Price"]);
        if (isNaN(entryPrice) || entryPrice <= 0) return false;
      }

      return true;
    }, [settings]);

   // --- Auto-calculate when required fields change ---
   useEffect(() => {
     if (canCalculate) {
       const delayDebounceFn = setTimeout(() => {
         runSummaryOnly(); // Instantly displays the table without saving to DB
       }, 400); 
       
       return () => clearTimeout(delayDebounceFn);
     } else {
       setCalculatedSummary(null); 
     }
   }, [
     canCalculate, 
     settings["Symbol"], 
     settings["Risk Mode"], 
     settings["Risk Dollar"], 
     settings["Risk Percentage"], 
     settings["Stop Loss"], 
     settings["Take Profit"], 
     settings["Entry Price"], 
     settings["Running Balance"],
     settings["Account Goal"],
     settings["Maximum Loss Limit"],
     settings["Stop Loss Mode"],
     settings["Take Profit Mode"]
   ]);

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-10">
      {/* Account Settings */}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 mb-4 animate-fadeIn">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row justify-between mb-4 gap-4">
          <div className="flex items-center gap-2">
            <IconGoal />
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
              Trade Setup (Prop Funded)
            </h2>
          </div>
                      {/* NEW STRATEGY CHECKLIST BUTTON */}
          <button 
            onClick={() => setShowStrategyModal(true)}
            className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-bold px-4 py-2 rounded-lg transition"
          >
            <FaClipboardList /> Strategy Checklist
          </button>
        </div>

        {/* --- INFO BANNER --- */}
        <div className="mb-6 text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-3 rounded-lg flex flex-wrap items-center justify-between gap-2">
          <span>Calculations are based on your <strong>effective balance</strong> (your maximum loss amount), not the full account size.</span>
          <button
            onClick={() => setShowLearnMorePopup(true)}
            className="font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Learn More
          </button>
        </div>

       {/* --- COLLAPSIBLE BALANCES SECTION --- */}
        <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6" open={isBalancesExpanded}>
          <summary 
            onClick={(e) => { e.preventDefault(); setIsBalancesExpanded(!isBalancesExpanded); }}
            className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center"
          >
            <div className="flex items-center gap-2">
              <span>Account Cash Status</span>
              <span className="text-sm font-normal text-gray-500">
                (Balance: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"])}</span> | Effective: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(settings["Prop Running Balance"] ?? settings["Running Balance"])}</span>)
              </span>
            </div>
            <span className={`text-blue-500 text-sm ${isBalancesExpanded ? 'hidden' : 'block'}`}>+ View Balances</span>
            <span className={`text-blue-500 text-sm ${isBalancesExpanded ? 'block' : 'hidden'}`}>- Hide</span>
          </summary>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-200 dark:bg-gray-800 bg-gray-50 dark:text-gray-200 cursor-not-allowed" 
              />
            </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
            <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Running Balance</label>
            <input type="number" value={settings["Running Balance"] ?? ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
          </div>

          {/* Effective Balance (Prop Running Balance) */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Effective Balance</label>
            <input 
              type="text"
              readOnly
              value={formatCurrency(settings["Prop Running Balance"] ?? 0)}
              className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-200 dark:bg-gray-800 bg-gray-50 dark:text-green-400 font-bold cursor-not-allowed" 
            />
          </div>
            
            {/* Maximum Loss Limit */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-xs font-bold text-gray-500 uppercase">Max Loss Limit</label>
                <Tooltip variant="blue" content={
                  <div className="space-y-2 p-1 max-w-xs">
                    <p className="text-[13px] leading-relaxed text-white">If your broker uses a trailing Maximum Loss Limit, check your prop account daily and update that amount here.</p>
                    <p className="text-[13px] leading-relaxed text-blue-200 font-semibold">This keeps your journal in sync with the amount that would blow your account.</p>
                  </div>
                }>
                  <span className="cursor-help text-[11px] text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                </Tooltip>
              </div>
              <input
                id="maxLossLimit"
                type="number"
                step="any"
                value={settings["Maximum Loss Limit"] ?? ""}
                onChange={(e) => handleSettingChange("Maximum Loss Limit", e.target.value)}
                className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-red-400 dark:bg-gray-700 dark:text-white"
              />
            </div>
            
          </div>
        </details>

        <div className="space-y-6">
          
          {/* --- STEP 1: TRADE SETUP --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Pair<span className="text-red-500">*</span></label>
                <select
                  value={settings["Symbol"] || ""}
                  onChange={e => handleSettingChange("Symbol", e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                >
                  <option value="">Select Symbol</option>
                  {favoriteForexOptions.length > 0 && (
                    <optgroup label="♥ Favorites">
                      {favoriteForexOptions.map(([v, label]) => (
                        <option key={v} value={v}>{label || v}</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="All Forex">
                    {nonFavoriteSymbols.map(([v, label]) => (
                      <option key={v} value={v}>{label || v}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              
              <div>
                <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
                  Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
                </label>
                <input 
                  type="number" 
                  step="any"
                  placeholder="e.g. 1.0500" 
                  value={settings["Entry Price"] || ""} 
                  onChange={e => handleSettingChange("Entry Price", e.target.value)} 
                  className={`no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${showEntryWarning ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"}`} 
                />
                {showEntryWarning && <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse"><FaExclamationTriangle size={10} /> Entry Price required for Price-based exits</p>}
              </div>
            </div>
          </div>

          {/* --- STEP 2: EXITS --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Stop Loss Group */}
              <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
                <div className="flex justify-between items-center mb-2">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500">*</span></label>
                  <select 
                    value={settings["Stop Loss Mode"] || "Pips"} 
                    onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                    className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                  >
                    <option value="Pips">by {fxUnit}</option>
                    {isIdx && <option value="Ticks">by Ticks</option>}
                    <option value="Price">by Price</option>
                  </select>
                </div>
                <input 
                  type="number" 
                  step="any"
                  placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : (settings["Stop Loss Mode"] === "Ticks" ? "Ticks" : fxUnit)} 
                  value={settings["Stop Loss"] || ""} 
                  onChange={e => handleSettingChange("Stop Loss", e.target.value)} 
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-red-400 dark:bg-gray-700 dark:text-white" 
                />
                {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
                  <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                    {settings["Stop Loss Mode"] === "Price" 
                      ? (() => {
                          const dist = Math.abs(settings["Entry Price"] - settings["Stop Loss"]) / (getPipSizeForSymbol(settings["Symbol"]) || 0.0001);
                          return `Est. Distance: ${Math.round(dist * 100) / 100} ${fxUnit} / ${isIdx ? Math.round(dist * 100) : Math.round(dist * 10 * 100) / 100} Ticks`;
                        })()
                      : `Est. Stop Price: ${(Number(settings["Entry Price"]) - ((settings["Stop Loss Mode"] === "Ticks" ? Number(settings["Stop Loss"]) / (isIdx ? 100 : 10) : Number(settings["Stop Loss"])) * (getPipSizeForSymbol(settings["Symbol"]) || 0.0001))).toFixed(isIdx ? 2 : 5)}`}
                  </p>
                )}
              </div>

              {/* Take Profit Group */}
              <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
                <div className="flex justify-between items-center mb-2">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
                  <select 
                    value={settings["Take Profit Mode"] || "Pips"} 
                    onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                    className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                  >
                    <option value="Pips">by {fxUnit}</option>
                    {isIdx && <option value="Ticks">by Ticks</option>}
                    <option value="Price">by Price</option>
                  </select>
                </div>
                <input 
                  type="number" 
                  step="any"
                  placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : (settings["Take Profit Mode"] === "Ticks" ? "Ticks" : fxUnit)} 
                  value={settings["Take Profit"] || ""} 
                  onChange={e => handleSettingChange("Take Profit", e.target.value)} 
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-green-400 dark:bg-gray-700 dark:text-white" 
                />
                {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
                  <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                    {settings["Take Profit Mode"] === "Price" 
                      ? (() => {
                          const dist = Math.abs(settings["Take Profit"] - settings["Entry Price"]) / (getPipSizeForSymbol(settings["Symbol"]) || 0.0001);
                          return `Est. Distance: ${Math.round(dist * 100) / 100} ${fxUnit} / ${isIdx ? Math.round(dist * 100) : Math.round(dist * 10 * 100) / 100} Ticks`;
                        })()
                      : `Est. Target Price: ${(Number(settings["Entry Price"]) + ((settings["Take Profit Mode"] === "Ticks" ? Number(settings["Take Profit"]) / (isIdx ? 100 : 10) : Number(settings["Take Profit"])) * (getPipSizeForSymbol(settings["Symbol"]) || 0.0001))).toFixed(isIdx ? 2 : 5)}`}
                  </p>
                )}
              </div>

            </div>
          </div>

          {/* --- STEP 3: RISK --- */}
          <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget</label>
                  <select 
                    value={settings["Risk Mode"] || "%"} 
                    onChange={e => handleSettingChange("Risk Mode", e.target.value)} 
                    className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                  >
                    <option value="%">% of Balance</option>
                    <option value="$">$ Amount</option>
                  </select>
                </div>
                {String(settings["Risk Mode"] || "%") === "%" ? (
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={
                        editingRiskPct
                          ? draftRiskPct
                          : (settings["Risk Percentage"] == null || settings["Risk Percentage"] === ""
                              ? ""
                              : String(settings["Risk Percentage"]))
                      }
                      onFocus={() => setEditingRiskPct(true)}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === "" || /^\d*\.?\d*$/.test(raw)) setDraftRiskPct(raw);
                      }}
                      onBlur={commitRiskPct}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="no-spin w-full pr-10 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-orange-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                      placeholder="0.00"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500 dark:text-gray-300">%</span>
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-500 dark:text-gray-300">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={
                        editingRiskDollar
                          ? draftRiskDollar
                          : (settings["Risk Dollar"] == null || settings["Risk Dollar"] === ""
                              ? ""
                              : String(settings["Risk Dollar"]))
                      }
                      onFocus={() => setEditingRiskDollar(true)}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === "" || /^\d*\.?\d*$/.test(raw)) setDraftRiskDollar(raw);
                      }}
                      onBlur={commitRiskDollar}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="no-spin w-full pl-8 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-orange-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- PROJECTIONS ROW --- */}
        <div className="col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 items-end mt-6 md:mt-6">
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input
              type="number"
              value={calculatedSummary && winProjection ? winProjection.tradeCount : ""}
              readOnly
              disabled
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-500 dark:text-white"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Win Projections
            </button>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${
                !calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
              }`}
              onClick={() => setLossProjectionOpen(true)}
              disabled={!calculatedSummary}
            >
              Loss Projections
            </button>
          </div>
        </div>

        {/* --- PROGRESS BARS --- */}
        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-6" />
        <div className="col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-gray-800 dark:text-gray-100">Daily Goal</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                {formatCurrency(dailyGoalProg.done, settings["Account Currency"])} /{" "}
                {formatCurrency(dailyGoalProg.target, settings["Account Currency"])}
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-3 bg-green-500 transition-all duration-500" style={{ width: `${dailyGoalProg.pct}%` }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-gray-800 dark:text-gray-100">Daily Loss Limit</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                {formatCurrency(dailyLossProg.lost, settings["Account Currency"])} /{" "}
                {formatCurrency(dailyLossProg.limit, settings["Account Currency"])}
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-3 bg-red-500 transition-all duration-500" style={{ width: `${dailyLossProg.pct}%` }} />
            </div>
          </div>

          <div className="col-span-1 md:col-span-2 mb-8">
            <div className="flex items-center justify-between mb-1">
              <span className="font-bold text-gray-800 dark:text-gray-100">Goal Progress</span>
              <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg relative overflow-hidden">
              <div className="bg-green-500 h-3 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>

        {/* --- SUMMARY SECTION --- */}
        <div className="max-w-lg mx-auto border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 p-4 mt-6">
          <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white gap-2"><IconGoal />Summary</h3>
          {calculatedSummary ? (
            <>
              <SummaryRow label="Reward to Risk Ratio" value={calculatedSummary.riskRewardRatio} />
              
              {String(settings["Risk Mode"] || "%") === "$" ? (
                <>
                  <SummaryRow
                    label="Risk Budget ($)"
                    value={formatCurrency(calculatedSummary.percentOfBalance, settings["Account Currency"])}
                  />
                  <SummaryRow
                    label="Equivalent % of Balance"
                    value={`${Number(calculatedSummary.riskPercent || 0).toFixed(2)}%`}
                  />
                </>
              ) : (
                <>
                  <SummaryRow
                    label={`${settings["Risk Percentage"]}% of Balance`}
                    value={formatCurrency(calculatedSummary.percentOfBalance, settings["Account Currency"])}
                  />
                </>
              )}

            {/* SEPARATED Stop Loss & Target Distance Rows */}
              <SummaryRow 
                label="Stop Loss" 
                value={calculatedSummary.stopAmt ? (() => {
                  const d = calculatedSummary.stopAmt;
                  const formatPts = Math.round(d * 100) / 100;
                  const formatTks = isIdx ? Math.round(d * 100) : Math.round(d * 10 * 100) / 100;
                  return isIdx ? `${formatPts} points / ${formatTks} ticks` : `${formatPts} pips / ${formatTks} ticks`;
                })() : "–"} 
              />
              <SummaryRow 
                label="Take Profit" 
                value={calculatedSummary.tpAmt ? (() => {
                  const d = calculatedSummary.tpAmt;
                  const formatPts = Math.round(d * 100) / 100;
                  const formatTks = isIdx ? Math.round(d * 100) : Math.round(d * 10 * 100) / 100;
                  return isIdx ? `${formatPts} points / ${formatTks} ticks` : `${formatPts} pips / ${formatTks} ticks`;
                })() : "–"} 
              />

              <SummaryRow
                label={
                  <span className="inline-flex items-center gap-2">
                    Total Risk
                    {calculatedSummary && (
                      <Tooltip
                        variant={(() => {
                          const stopLossPips = Number(calculatedSummary.stopLoss || 0);
                          const suggested    = Number(calculatedSummary.lots || 0);
                          const minLot       = Math.max(0.01, Number(calculatedSummary.minLot || settings["Min Lot Size"] || 0.01));
                          const usedLots     = suggested > 0 ? suggested : minLot;

                          const pipVal       = Number(calculatedSummary.pipValue || settings["Pip Value"] || getPipValue(settings["Symbol"], 1, settings["Account Currency"]));
                          const riskUsed     = stopLossPips * usedLots * pipVal;

                          const balanceNow   = Number(settings["Running Balance"] || 0);
                          const riskPctDisp  = Number(settings["Risk Percentage"] || 0);
                          const riskBudget   = Number(calculatedSummary.percentOfBalance || (balanceNow * (riskPctDisp / 100)));

                          if (usedLots <= 0) return "neutral";
                          return riskUsed <= riskBudget ? "blue" : "red";
                        })()}
                        backdrop
                        content={(() => {
                          const currency      = settings["Account Currency"] || "USD";
                          const stopLossPips  = Number(calculatedSummary.stopLoss || 0);
                          const suggested     = Number(calculatedSummary.lots || 0);
                          const minLot        = Math.max(0.01, Number(calculatedSummary.minLot || settings["Min Lot Size"] || 0.01));
                          const usedLots      = suggested > 0 ? suggested : minLot;

                          const pipVal        = Number(calculatedSummary.pipValue || settings["Pip Value"] || getPipValue(settings["Symbol"], 1, settings["Account Currency"]));
                          const riskUsed      = stopLossPips * usedLots * pipVal;

                          const balanceNow    = Number(settings["Running Balance"] || 0);
                          const riskPctDisp   = Number(settings["Risk Percentage"] || 0);
                          const riskBudget    = Number(calculatedSummary.percentOfBalance || (balanceNow * (riskPctDisp / 100)));

                          const equalsBudget  = Math.abs(riskUsed - riskBudget) < 0.01;
                          const isZero        = usedLots <= 0;
                          const baseUnit      = isIdx ? "points" : "pips";

                          return (
                            <div className={`space-y-2 ${isZero ? "text-red-600 dark:text-red-400" : ""}`}>
                              <div
                                className={
                                  "text-sm uppercase tracking-wide font-bold opacity-100 " +
                                  (isZero ? "text-red-700 dark:text-red-500" : "text-blue-700 dark:text-blue-400")
                                }
                              >
                                How we calculate total risk
                              </div>

                              <div className="font-sans text-[16px]">
                                Stop Loss ({baseUnit}) × Suggested Lots × Pip/Point Value
                              </div>

                              <div className="font-sans text-[16px]">
                                {stopLossPips.toFixed(2)} {baseUnit} × {usedLots.toFixed(2)} lot{usedLots === 1 ? "" : "s"} × {formatCurrency(pipVal, currency)}
                                {isZero && <span className="opacity-80"> (min)</span>}{" "}
                                = <strong>{formatCurrency(riskUsed, currency)}</strong>
                              </div>

                              {isZero && (
                                <p className="text-[13px]">
                                  Suggested lots is <span className="font-semibold">0</span>, so we use your minimum:{" "}
                                  <span className="font-semibold">{minLot.toFixed(2)}</span>.
                                </p>
                              )}

                              <div className="font-sans text-[16px]">
                                Risk budget: <strong>{formatCurrency(riskBudget, currency)}</strong>{" "}
                              </div>

                              <div
                                className={`mt-1 text-[16px] font-semibold ${
                                  equalsBudget
                                    ? "text-green-300"
                                    : (riskUsed <= riskBudget ? "text-green-300" : "text-red-400 dark:text-red-400")
                                }`}
                              >
                                {equalsBudget
                                  ? "✓ Total risk = budget"
                                  : riskUsed <= riskBudget
                                    ? "✓ Total risk ≤ budget"
                                    : "✕ Total risk > budget"}
                              </div>
                            </div>
                          );
                        })()}
                      >
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                          ?
                        </span>
                      </Tooltip>
                    )}
                  </span>
                }
                value={formatCurrency(calculatedSummary.totalRisk, settings["Account Currency"])}
                red={
                  Number(calculatedSummary.totalRisk || 0) >
                  Number(
                    calculatedSummary.percentOfBalance ||
                    (Number(settings["Running Balance"] || 0) *
                      (Number(settings["Risk Percentage"] || 0) / 100))
                  )
                }
              />

              <SummaryRow label="Target Reward" value={formatCurrency(calculatedSummary.targetReward, settings["Account Currency"])} />
              
              {(() => {
                 const baseUnit = isIdx ? "points" : "pips";
                 return (
                   <SummaryRow
                     label="Maximum Stop Loss"
                     value={maxStopLossValue > 0 ? `${maxStopLossValue} ${baseUnit} (${maxStopLossValue * 10} ticks)` : ""}
                     red={isTightStop}
                   />
                 );
              })()}

              <div
                className={
                  Number(calculatedSummary.lots || 0) === 0
                    ? "rounded-md px-2 py-1 bg-red-50 dark:bg-red-900/40"
                    : "rounded-md px-2 py-1 bg-blue-300 dark:bg-blue-400/30"
                }
              >

              {/* --- NEW: POSITION SIZE (UNITS) ROW --- */}
              <SummaryRow
                label={`Position Size (${calculatedSummary.unitsLabel || 'Units'})`}
                value={
                  Number(calculatedSummary.lots || 0) > 0
                    ? Number(calculatedSummary.units || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : ""
                }
              />  
                <SummaryRow
                  label={<span className="text-gray-600 dark:text-gray-300">Suggested Lots</span>}
                  value={
                    Number(calculatedSummary.lots || 0) === 0
                      ? <span className="text-red-600 font-bold">0</span>
                      : <span className="text-gray-600 dark:text-gray-300 font-semibold">
                          {Number(calculatedSummary.lots).toFixed(2)}
                        </span>
                  }
                  red={Number(calculatedSummary.lots || 0) === 0}
                />
              </div>

            </>
          ) : (
            <div className="text-gray-400 dark:text-gray-600 text-center py-6">Summary will appear as you type.</div>
          )}
        </div>

        {/* Save Settings Button */}
        <div className="flex gap-4 justify-end mt-8">
          <button
            type="button"
            onClick={() => handleCalculate(true)}
            disabled={savingSettings || !settings["Symbol"]}
            className={`bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-800 dark:to-blue-500 
              text-white font-semibold px-6 py-3 rounded-xl shadow-lg transition 
              ${savingSettings || !settings["Symbol"]
                ? "opacity-50 cursor-not-allowed"
                : "hover:from-blue-700 hover:to-blue-500 dark:hover:from-blue-900 dark:hover:to-blue-700"
              }`}
          >
            {savingSettings ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>

 {/* Journal Entry - Logical Grouping Layout */}
      {!settings["Calculator Only"] && (
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        
        {/* Header */}
        <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
          <IconJournal />
          <div>
            <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Log your trade details below. <span className="text-red-500">* denotes required</span>
            </p>
          </div>
        </div>

        <div className="space-y-8"> 

          {/* SECTION 1: SETUP & CONTEXT */}
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">
              1. Setup & Context
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Date */}
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  value={journal["Date"] || getTodayISO()} 
                  onChange={e => handleJournalChange("Date", e.target.value)} 
                  className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
                />
              </div>
              
              {/* Session */}
              {!settings["Hide Session"] && (
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
                <select 
                  value={journal["Session"]} 
                  onChange={e => handleJournalChange("Session", e.target.value)} 
                  className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                >
                  <option value="">Select</option>
                  {SESSION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              )}

              {/* Timeframe */}
              <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
                 <input 
                   type="text" 
                   list="timeframe-options-fx" 
                   value={journal["Timeframe"]} 
                   onChange={e => handleJournalChange("Timeframe", e.target.value)} 
                   placeholder="e.g. 5m"
                   className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
                 />
                 <datalist id="timeframe-options-fx">
                    {TIMEFRAME_OPTIONS_FX.map(tf => <option key={tf} value={tf} />)}
                 </datalist>
              </div>

              {/* Position */}
              <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
                 <select 
                   value={journal["Position"]} 
                   onChange={e => handleJournalChange("Position", e.target.value)} 
                   className="w-full px-4 py-2 h-[42px] rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                 >
                  <option value="">Select</option>
                  {POSITION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 2: EXECUTION */}
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">
              2. Execution
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Entry Group */}
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
                <div className="space-y-4">
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                     <div className="relative flex items-center">
                        <input 
                          id="entryTime" 
                          type="time" 
                          ref={entryTimeRef} 
                          value={to24HourTime(journal["Entry Time"])} 
                          onChange={e => {
                            const rawTime24 = e.target.value;
                            setJournal(prev => {
                              const nextState = { ...prev, "Entry Time": toAmPm(rawTime24) };
                              
                              // Auto-populate Session only if it is currently empty
                              if (!prev.Session || prev.Session === "") {
                                const calculatedSession = getSessionFromTime(rawTime24);
                                if (calculatedSession) {
                                  nextState.Session = calculatedSession;
                                }
                              }
                              
                              return nextState;
                            });
                          }} 
                          className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" 
                        />
                        <FaRegClock
                          className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                          onClick={() => entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()}
                        />
                     </div>
                   </div>

                   {!settings["Hide Entry Price"] && (
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
                     <input 
                       type="number" 
                       placeholder="0.00000" 
                       value={journal["Entry Price"] ?? ""}
                       onChange={e => handleJournalChange("Entry Price", e.target.value)}
                       className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                     />
                   </div>
                   )}

                   {!settings["Hide Entry Strategy"] && (
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                     <input 
                       type="text" 
                       list="entry-strategy-options"
                       value={journal["Entry Strategy"]} 
                       onChange={e => handleJournalChange("Entry Strategy", e.target.value)} 
                       className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                     />
                     <datalist id="entry-strategy-options">
                        {entryStrategies.map((strategy) => <option key={strategy} value={strategy} />)}
                     </datalist>
                   </div>
                   )}
                </div>
              </div>

              {/* Exit Group */}
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
                <div className="space-y-4">
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                     <div className="relative flex items-center">
                        <input 
                          type="time" 
                          ref={exitTimeRef}
                          value={to24HourTime(journal["Exit Time"])}
                          onChange={e => setJournal(prev => ({ ...prev, "Exit Time": e.target.value }))}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                        />
                        <FaRegClock
                          className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                          onClick={() => exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()}
                        />
                     </div>
                   </div>

                   {!settings["Hide Exit Price"] && (
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
                     <input 
                       type="number" 
                       placeholder="0.00000" 
                       value={journal["Exit Price"] ?? ""}
                       onChange={e => handleJournalChange("Exit Price", e.target.value)}
                       className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                     />
                   </div>
                   )}

                   {!settings["Hide Exit Strategy"] && (
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                     <input 
                       type="text" 
                       list="exit-strategy-options"
                       value={journal["Exit Strategy"]} 
                       onChange={e => handleJournalChange("Exit Strategy", e.target.value)} 
                       className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition" 
                     />
                     <datalist id="exit-strategy-options">
                        {exitStrategies.map((strategy) => <option key={strategy} value={strategy} />)}
                     </datalist>
                   </div>
                   )}
                </div>
              </div>

              {/* Calculated Stats Group */}
               <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Quantiy Details</label>
                <div className="space-y-4">
                   <div>
                      <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                        Actual Lot Size<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          journal["Size"] !== undefined && journal["Size"] !== ""
                            ? journal["Size"]
                            : calculatedSummary?.lots ?? ""
                        }
                        onChange={e => handleJournalChange("Size", e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                        Pips Captured
                      </label>
                      <input
                        id="pips"
                        type="number"
                        value={journal["Pips"] || ""}
                        onChange={e => handleJournalChange("Pips", e.target.value)}
                        className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                        placeholder="Qty"
                      />
                    </div>

                    {!settings["Hide Potential"] && (
                    <div> 
                      <label className="block font-bold text-sm mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2"> 
                        Potential Pips 
                        <Tooltip 
                          variant="neutral" 
                          appendTo={typeof window !== "undefined" ? document.body : undefined} // [!code ++]
                          content={<div className="text-sm">This field captures the potential pips that could have been captured with the trade.</div>} 
                        > 
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help"> 
                            ? 
                          </span> 
                        </Tooltip>
                      </label> 
                      <input 
                        id="potential" 
                        type="number" 
                        value={journal["Potential"] || ""} 
                        onChange={e => handleJournalChange("Potential", e.target.value)} 
                        className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono" 
                        placeholder="Qty" 
                      /> 
                    </div>
                    )}
                  </div>
               </div>

            </div>
          </div>

          {/* SECTION 3: OUTCOME */}
          <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">
              3. Outcome
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
              
              {/* Win/Loss */}
              <div>
                <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">Result<span className="text-red-500">*</span></label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setJournal(j => ({ ...j, Win: true, Loss: false }))}
                    className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                      journal["Win"] 
                        ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                        : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    WON
                  </button>
                  <button
                    type="button"
                    onClick={() => setJournal(j => ({ ...j, Win: false, Loss: true }))}
                    className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
                      journal["Loss"] 
                        ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                        : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600"
                    }`}
                  >
                    LOSS
                  </button>
                </div>
              </div>

              {/* New Balance */}
              <div>
                 <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance<span className="text-red-500">*</span></label>
                 <div className="relative">
                   <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                   <input 
                     type="text" 
                     inputMode="decimal" 
                     value={journal["Actual Balance"] ?? ""} 
                     onChange={e => setJournal(j => ({ ...j, ["Actual Balance"]: e.target.value }))} 
                     onBlur={(e) => {
                        const n = parseCurrency(e.target.value);
                        setJournal(j => ({ ...j, ["Actual Balance"]: n }));
                     }}
                     className="w-full pl-8 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-500 focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner" 
                     placeholder="0.00"
                   />
                 </div>
              </div>

              {/* NEW: Target Reached Checkbox */}
                    <div className="mt-6 flex items-center bg-green-50 dark:bg-green-900/10 p-3 rounded-xl border border-green-100 dark:border-green-800">
                       <input 
                           type="checkbox" 
                           id="targetReachedFut" 
                           checked={!!journal["Target Reached"]} 
                           onChange={e => handleJournalChange("Target Reached", e.target.checked)} 
                           className="mr-3 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer" 
                       />
                       <label htmlFor="targetReachedFut" className="text-sm font-bold text-green-700 dark:text-green-400 cursor-pointer">
                           Target Reached?
                       </label>
                    </div>
            </div>
          </div>

          {/* SECTION 4: PSYCHOLOGY & REVIEW */}
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">
              4. Psychology & Review
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              {/* Rules */}
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?<span className="text-red-500">*</span></label>
                  <select 
                    value={journal["Did I follow my Rules?"]} 
                    onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)} 
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select</option>
                    {RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>

              {/* Mood */}
              {!settings["Hide Mood"] && (
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
                  <select 
                    value={journal["Mood_Mindset"]} 
                    onChange={e => handleJournalChange("Mood_Mindset", e.target.value)} 
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select (1=Calm, 5=Stress)</option>
                    {MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>
              )}

              {/* Grade */}
              {!settings["Hide Grade"] && (
              <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
                  <select 
                    value={journal["Grade"]} 
                    onChange={e => handleJournalChange("Grade", e.target.value)} 
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
                  >
                    <option value="">Select</option>
                    {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
              </div>
              )}
            </div>

            {/* Inputs */}
            <div className="space-y-6">
               <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
                 <input 
                   type="text" 
                   value={journal["Links_Screenshots"]} 
                   onChange={e => handleJournalChange("Links_Screenshots", e.target.value)} 
                   placeholder="Paste URL here..."
                   className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
                 />
               </div>
               
               <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
                 <textarea 
                   rows={3} 
                   value={journal["Notes"]} 
                   onChange={e => handleJournalChange("Notes", e.target.value)} 
                   placeholder="Anything to note about this trade?"
                   className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
                 />
               </div>

               <div>
                 <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                   Emotional Notes
                 </label>
                 <textarea 
                   rows={3} 
                   value={journal["Emotional Notes"] || ""} 
                   onChange={e => handleJournalChange("Emotional Notes", e.target.value)} 
                   placeholder="How were you feeling during this execution? Anxious? Confident? Rushed?"
                   className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
                 />
               </div>

               <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">
                    Lesson (Optional)
                  </label>
                  <textarea 
                    rows={2} 
                    value={journal["Lesson"]} 
                    onChange={e => handleJournalChange("Lesson", e.target.value)} 
                    placeholder="What did you learn from this trade?"
                    className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y" 
                  />
                  <div className="mt-3">
                    <label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                      <input 
                        type="checkbox" 
                        checked={journal["LogLesson"]} 
                        onChange={e => handleJournalChange("LogLesson", e.target.checked)} 
                        className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" 
                      />
                      Save to Lessons Database
                    </label>
                  </div>
               </div>
            </div>
          </div>
        
        </div>

        {/* Save Button */}
        <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
           <button 
             className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-600 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
             onClick={handleSaveTrade} 
             disabled={savingTrade}
           >
              {savingTrade ? "Saving..." : "Save Trade"}
           </button>
        </div>

      </div>
)} {/* <--- ADD THIS CLOSING BRACE/PARENTHESIS HERE */}

      
      {/* === Overlays & Popups (now INSIDE component render) === */}

    {projectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
      max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800"
        onClick={() => setProjectionOpen(false)}
        aria-label="Close"
      >×</button>
        
        <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
            <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
            <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Win Projection Table
        </h3>

        {/* --- EXPLANATORY TEXT (Forex Adapted) --- */}
        <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
          Projections show how many trades (using at least your minimum lot size setting) it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. The totals are estimates and don't take into account broker fees and commissions.
          <br /><br />
          If you prefer to always trade the same lot size, you can set the minimum and maximum lot size to the same number in the settings.
        </p>

        {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
                    <div className="mb-4 flex items-center justify-end">
                      <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                        <span className="mr-3">Group by Trading Day</span>
                        <div className="relative flex items-center">
                          <input 
                            type="checkbox" 
                            className="sr-only" 
                            checked={showDaySeparators} 
                            onChange={() => setShowDaySeparators(!showDaySeparators)} 
                          />
                          <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                          <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                        </div>
                      </label>
                    </div>
                    {/* 👆 END TOGGLE BLOCK 👆 */}
        
        {/* --- DYNAMIC TIME ESTIMATION BLOCK --- */}
          {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const trades      = winProjection?.trades || [];
              const tradeCount = winProjection?.tradeCount || 0;

              // 1. Check if account is blown (Prop Firm Max Loss or $0)
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const maxLossAbs = Math.abs(maxLoss);
              const ruinThreshold = maxLossAbs > 0 ? init - maxLossAbs : 0;

              // If blown, return ONLY the red warning. This hides the blue stats box.
              if (running <= ruinThreshold) {
                return (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm font-bold text-red-800 dark:text-red-200">
                    Your account balance has reached your maximum loss limit. Switch to your demo account to review your plan.
                  </div>
                );
              }

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

          // 2. Calculate "Trading Days" using the Hybrid Logic
          //    - If Trade Profit < Daily Goal: Accumulate trades until Daily Goal is met (counts as 1 day).
          //    - If Trade Profit >= Daily Goal: Count 1 Trade as 1 Day.
          let estimatedDays = 0;
          let currentDayAccumulator = 0;

          if (dailyGoal > 0) {
            for (const t of trades) {
              const profit = t.tradeProfit || 0;
              
              if (profit >= dailyGoal) {
                // One trade makes the day
                estimatedDays++;
                currentDayAccumulator = 0; 
              } else {
                // Accumulate small trades to make a day
                currentDayAccumulator += profit;
                if (currentDayAccumulator >= dailyGoal) {
                  estimatedDays++;
                  currentDayAccumulator = 0; // Day complete
                }
              }
            }
            // Count any partial progress at the end as a final day
            if (currentDayAccumulator > 0) estimatedDays++;
          } else {
            // Fallback if no Daily Goal set: 1 trade = 1 day (Conservative)
            estimatedDays = tradeCount;
          }

          return (
            <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <FaRegClock className="text-blue-500" />
                Estimated Time to Goal
              </h4>
              
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <div className="flex-1">
                  <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">winning trades in ≈ </span> {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span> 
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades in about {estimatedDays} if you reached your trading daily goal.
                  </p>
                </div>

                <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                  <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>
                      While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.
                    </li>
                    <li>
                      Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.
                    </li>
                    <li>
                          As your balance compounds, your position sizes and profits will <strong>increase</strong>. Make sure to update your <strong>Daily Goal</strong> in the settings to reflect your growing account size!
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}
        {/* --- END DYNAMIC BLOCK --- */}

        {winProjection?.tradeCount === 0 && (
              <div className={`text-center py-4 font-semibold ${
                (() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const mAbs = Math.abs(m);
                  const thresh = mAbs > 0 ? i - mAbs : 0;
                  return r <= thresh;
                })() 
                  ? "text-red-600 dark:text-red-400" 
                  : "text-green-600 dark:text-green-300"
              }`}>
                {(() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const mAbs = Math.abs(m);
                  const thresh = mAbs > 0 ? i - mAbs : 0;
                  if (r <= thresh) {
                    return "No trades can be taken. Your account is blown. Please consider switching to a demo account and refine your strategy before purchasing another challenge or resetting your account.";
                  }
                  return "No trades can be taken with the current settings. If you have reached your goal you can adjust it and continue trading.";
                })()}
              </div>
            )}


      {winProjection?.tradeCount > 0 && (() => {
              // Only allow the table to render if the account is NOT blown
        const r = Number(settings["Running Balance"] || 0);
        const i = Number(settings["Initial Balance"] || 0);
        const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
        const mAbs = Math.abs(m);
        const thresh = mAbs > 0 ? i - mAbs : 0;
        return r > thresh; 
            })() && (
      <div className="overflow-auto max-h-[50vh]">
        <table className="min-w-full border-separate border-spacing-0 rounded-xl shadow bg-white/95 dark:bg-gray-900/90 border-2 border-blue-200 dark:border-blue-800">
          <thead>
            <tr className="bg-blue-100 dark:bg-blue-900/80">
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Max Drawdown</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Lot Size</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Risk $</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade Profit</th>
              <th className="px-3 py-2 border-b-2 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
            </tr>
          </thead>
          <tbody>
                    {(() => {
                      // Keep your existing runningBal setup if it exists in the file
                      let runningBal = Number(settings["Running Balance"] || settings["Beginning Balance"] || 0); 
                      const rowsToShow = isTrialing 
                                      ? winProjection.trades.slice(0, 10) 
                                      : winProjection.trades;

                      // 1. Setup Day Trackers
                      let currentDay = 1;
                      let dayAccumulator = 0;
                      const dailyGoalAmt = Number(settings["Daily Goal"] || 0);

                      // 2. Pre-process trades to assign them a "Day Number"
                      const tradesWithDays = rowsToShow.map((row) => {
                        const assignedDay = currentDay;
                        if (dailyGoalAmt > 0) {
                          dayAccumulator += (row.tradeProfit || 0);
                          if (dayAccumulator >= dailyGoalAmt) {
                            currentDay++;
                            dayAccumulator = 0;
                          }
                        }
                        return { ...row, day: assignedDay };
                      });

                      // 3. Render rows with visual breaks
                      return tradesWithDays.map((row, i) => {
                        
                        // Keep your existing before/after balance logic here:
                        const before = runningBal;
                        const after  = before + (row.tradeProfit ?? 0);
                        runningBal = after;

                        // Check if the Day Number changed
                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDays[i-1].day;

                        return (
                          <React.Fragment key={i}>
                            {/* --- THE DAY DIVIDER --- */}
                            {isNewDay && (
                              <tr className="bg-blue-100/50 dark:bg-blue-900/30">
                                <td colSpan="10" className="px-3 py-2 text-center text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-widest border-y border-blue-200 dark:border-blue-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            
                             <tr
                    key={i}
                    className={`${
                      row.underfunded ? "bg-red-100 dark:bg-red-900/80" : "hover:bg-blue-50 dark:hover:bg-blue-800/80"
                    } transition`}
                  >
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{row.trade}</td>
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(before, settings["Account Currency"])}</td>
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">
                      {formatCurrency(
                        row.riskDollar ?? (row.lots * Number(settings["Stop Loss"] || 0) * Number(pipValue || 0)),
                        settings["Account Currency"]
                      )}
                    </td>
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">
                      {Number(row.lots ?? 0).toFixed(2)}
                    </td>
                    <td className={`px-3 py-2 border-b ${row.underfunded ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                      {formatCurrency(row.totalRisk ?? 0, settings["Account Currency"])}
                    </td>
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit ?? 0, settings["Account Currency"])}</td>
                    <td className="px-3 py-2 border-b text-gray-900 dark:text-gray-100">{formatCurrency(after, settings["Account Currency"])}</td>
                  </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
  
        </table>
      {/* --- NEW: Trial Indicator --- */}
              {isTrialing && winProjection.trades.length > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
            )}
    </div>
  </div>
)}

{lossProjectionOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
    <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
      max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
      <button
        className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800"
        onClick={() => setLossProjectionOpen(false)}
        aria-label="Close"
      >×</button>

      <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
          <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
          <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Loss Projection Table
      </h3>

      <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
        Projections show how many consecutive full-loss trades (using at least your minimum lot size setting) it would take to blow your account. The totals are estimates and do not include fees.
        <br /><br />
        If you prefer to always trade the same lot size, you can set the minimum and maximum lot size to the same number in the settings.
      </p>

      {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
            {/* 👆 END TOGGLE BLOCK 👆 */}

      {/* --- DYNAMIC SUMMARY BLOCK --- */}
      {(() => {
        const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
        const init      = Number(settings["Initial Balance"] || 0);
        const maxLoss = Math.abs(Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0));
        const running   = Number(settings["Running Balance"] || 0);
        const tradeCount = lossProjection?.tradeCount || 0;

        if (!(dailyLoss > 0)) return null;


        // Logic for Prop (Max Loss) vs Retail ($0 Balance)
                let daysToRuin = 0;
                let ruinThreshold = 0;
                // Allow Prop logic even if Initial Balance is 0 (e.g. funded account tracking PnL)
                let isProp = (maxLoss > 0);

                if (isProp) {
                  if (init > 0) {
                     // Standard Prop: determine if maxLoss is an amount or a threshold
                     ruinThreshold = Math.max(maxLoss, init - maxLoss);
                  } else {
                     // Funded/Zero-based: maxLoss is treated as the allowed drawdown amount
                     ruinThreshold = init - maxLoss;
                  }
                  
                  const distance = Math.max(0, running - ruinThreshold);
                  daysToRuin = Math.ceil(distance / dailyLoss);
                }

        return (
          <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
            <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
              Risk to Ruin Analysis
            </h4>
            
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="flex-1">
                <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                  {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                  To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                </p>
              </div>

              <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                </div>
                <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                  If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
        <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
          <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Lot Size</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Loss Amount</th>
              <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
            </tr>
          </thead>
          <tbody>
                  {(lossProjection?.trades?.length ?? 0) === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                        No trades can be taken with the current settings.
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const rowsToShow = isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades;
                      
                      // 1. Setup Day Trackers
                      let currentLossDay = 1;
                      let lossAccumulator = 0;
                      const dailyLossAmt = Number(settings["Daily Loss Limit"] || 0);

                      // 2. Pre-process trades
                      const tradesWithDaysLoss = rowsToShow.map(row => {
                        const assignedDay = currentLossDay;
                        if (dailyLossAmt > 0) {
                          lossAccumulator += (row.loss || 0);
                          if (lossAccumulator >= dailyLossAmt) {
                            currentLossDay++;
                            lossAccumulator = 0; // Reset for the next day
                          }
                        }
                        return { ...row, day: assignedDay };
                      });

                      // 3. Render rows
                      return tradesWithDaysLoss.map((row, i) => {
                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDaysLoss[i-1].day;

                        return (
                          <React.Fragment key={i}>
                            {/* --- THE DAY DIVIDER --- */}
                            {isNewDay && (
                              <tr className="bg-red-100/50 dark:bg-red-900/30">
                                <td colSpan="10" className="px-4 py-2 text-center text-xs font-bold text-red-800 dark:text-red-300 uppercase tracking-widest border-y border-red-200 dark:border-red-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            
                            <tr key={i} className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group">
                              <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">{row.trade}</td>
                              <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                                {formatCurrency(row.balanceBefore, settings["Account Currency"])}
                              </td>
                              <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                                {Number(row.lots ?? 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                                {formatCurrency(row.loss, settings["Account Currency"])}
                              </td>
                              <td className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 text-gray-900 dark:text-gray-100">
                                {formatCurrency(row.balanceAfter, settings["Account Currency"])}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </tbody>
        </table>
              {/* --- NEW: Trial Indicator --- */}
              {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRiskWarningPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => setShowRiskWarningPopup(false)} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
                    </svg>
              High Risk Warning!
            </h2>

            <p className="mb-3 text-gray-800 dark:text-gray-100">
              <span className="font-bold">Based on your Balance, Stop Loss, Risk and Minimum Lot Size, the </span>
              <span className="font-bold text-red-600">Total Risk</span> on this trade (
              <span className="font-bold text-red-600">{formatCurrency(calculatedSummary?.totalRisk, settings["Account Currency"])}</span>) is higher than your risk allowance (
              <span className="font-bold text-red-600">{formatCurrency(calculatedSummary?.percentOfBalance, settings["Account Currency"])}</span>)!
            </p>

            {maxStopLossValue > 0 && (
              <p className="mb-3 text-gray-800 dark:text-gray-100">
                <span className="font-semibold">Maximum Stop Loss</span> size to stay within your risk:{" "}
                <span className="font-bold text-red-600">{maxStopLossValue}</span> pips.
              </p>
            )}
            {Number(calculatedSummary?.minLot ?? settings["Min Lot Size"]) > 0 && (
              <p className="mb-3 text-gray-800 dark:text-gray-100">
                <span className="font-semibold">Minimum Lot Size</span>:{" "}
                <span className="font-bold text-red-600">
                  {Number((calculatedSummary?.minLot ?? settings["Min Lot Size"]) ?? 0.01).toFixed(2)}
                </span>
              </p>
            )}

            <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">Consider a different symbol or wait for another trade setup that requires a smaller stop loss to stay within your risk management rules.</p>
            <button onClick={() => setShowRiskWarningPopup(false)} className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

{/* --- ADD THIS ENTIRE POPUP MODAL --- */}
{showLearnMorePopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowLearnMorePopup(false)}
        aria-label="Close"
      >
        ×
      </button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"></path>
        </svg>
        Understanding Prop Firm Calculations
      </h2>
      <div className="space-y-4 text-gray-700 dark:text-gray-300">
        <p>
          In a prop firm evaluation, your risk should be calculated based on your "effective balance", which is the actual amount of capital you can lose before failing the challenge.
        </p>
        <p className="font-semibold text-gray-800 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 p-2 rounded">
          Effective Balance = Maximum Loss Amount
        </p>
        <p>
          For example, on a $100,000 account with a $5,000 maximum loss amount, your effective balance for risk calculations is $5,000. A 2% risk on this is $100, not $2,000.  As the account balance grows the Effective Balance will grow.
        </p>
        <p>
          This tool uses your effective balance to suggest lot sizes, helping you stay within the firm's drawdown rules and not overleverage and blow your account too quickly.
        </p>
      </div>
      <button
        onClick={() => setShowLearnMorePopup(false)}
        className="mt-6 bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition w-full"
      >
        OK
      </button>
    </div>
  </div>
)}


{showTradeSaved && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
            
            {/* ✅ FIX: Add the missing icon, title, and message content here */}
            <div className="mb-4">
              <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
                <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
                  <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              </span>
            </div>
            <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">Trade Saved!</div>
            <div className="text-gray-700 dark:text-gray-200 mb-4 text-center">Your journal entry has been saved.</div>
            
            <button 
              className="mt-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-lg transition" 
              onClick={handleCloseSaveConfirmation}
            >
              OK
            </button>
          </div>
        </div>
      )}


      {currentPopup?.id === 'dailyGoalPopup' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          {/* Confetti for celebration! */}
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>

            <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
              <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Congratulations!
            </h2>

            <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
              You reached your{" "}
              <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!
              <br />
              {currentPopup.data?.dailyGoal !== undefined && (
                <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
                  Goal:{" "}
                  <span className="text-green-700 dark:text-green-500">
                    {Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {
                      style: "currency",
                      currency: "USD",
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </span>
              )}

              <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
                Take a moment to celebrate your discipline and progress.<br /><br />
                Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
                {/* Dynamic day-based message (local time). Pass a TZ if you prefer: getDailyGoalMessage('America/New_York') */}
                {getDailyGoalMessage()}
              </span>
            </div>

            <button
              onClick={closeCurrentPopup}
              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}



{currentPopup?.id === "dailyLossLimitExceeded" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => {
          closeCurrentPopup();
        }}
        aria-label="Close"
      >
        ×
      </button>

      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
        </svg>
        Daily Loss Limit Reached
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are down <span className="font-bold text-red-600">
          {formatCurrency(currentPopup?.data?.lostBeyondWins || 0, settings["Account Currency"])}</span> beyond today’s gains.
      </p>

      <p className="mb-4 text-gray-700 dark:text-gray-300">
        Your daily loss limit is <span className="font-bold">
          {formatCurrency(currentPopup?.data?.limit || 0, settings["Account Currency"])}</span>.
            </p>
              <p className="mb-4 text-gray-700 dark:text-gray-300">
        Consider stopping for the day and come back tomorrow.  The key is avoiding going on tilt and revenge trading.
       </p>
      <button
        onClick={() => {
          closeCurrentPopup();
        }}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


  {currentPopup?.id === 'overtradingPopup' && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn"
        role="dialog"
        aria-modal="true"
        aria-labelledby="overtrading-title"
        aria-describedby="overtrading-desc"
      >
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          onClick={closeCurrentPopup}
          aria-label="Close"
        >
          ×
        </button>

        <h2
          id="overtrading-title"
          className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center"
        >
          <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
          </svg>
          Warning: Overtrading!
        </h2>

        <p className="mb-4 text-gray-800 dark:text-gray-100">
          <span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span>
        </p>

        <p className="mb-4 text-gray-800 dark:text-gray-100">
          <span className="font-bold">Your maximum trades per day is: </span>
          <span className="font-bold text-yellow-700 dark:text-yellow-300">
            {currentPopup.data.maxTradesPerDay}
          </span>
        </p>

        <p id="overtrading-desc" className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
          Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.
        </p>

        <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
          Review your trades, lock your account, and come back tomorrow recharged for a strong start!
        </p>

        <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
          Below is a really good video from Psychology of Profits regarding protecting your mental capital.
        </p>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3 justify-end">
          <a
            href="https://www.youtube.com/watch?v=6vZG4epFEOM"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition"
          >
            {/* Play icon */}
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
            Watch: Mental Capital
            <span className="sr-only">(opens in a new tab)</span>
          </a>

          <button
            onClick={closeCurrentPopup}
            className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>

  )}


      {currentPopup?.id === 'tooManyLossesPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Daily Lost Trades Limit Reached!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You've hit your max daily loss count.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum losses per day is: </span>
        <span className="font-bold text-red-700 dark:text-red-300">
          {currentPopup.data.maxLossesPerDay}
        </span>
      </p>
      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
        Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


      {currentPopup?.id === "maxLossLimitBlown" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>

      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-400 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Account Blown 
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You’ve reached the maximum loss limit for this account.
      </p>

      <p className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        <span className="font-semibold text-red-600">Start:</span>{" "}
        <span className="font-bold text-red-600">
          {formatCurrency(currentPopup?.data?.base, settings["Account Currency"])}
        </span>{" "}
        <br/> <span className="font-semibold text-red-600">Max Loss:</span>{" "}
        <span className="font-bold text-red-600">
          {formatCurrency(currentPopup?.data?.maxLoss, settings["Account Currency"])}
        </span>{" "}
        <br/> <span className="font-semibold text-red-600">Current:</span>{" "}
        <span className="font-bold text-red-600">
          {formatCurrency(currentPopup?.data?.rb, settings["Account Currency"])}
        </span>
      </p>

      <p className="font-semibold mb-6 text-gray-700 dark:text-gray-300">
        Per the Challenge rules, once you hit the Maximum Loss Limit the challenge is failed.
      </p>
      <p className="font-semibold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        Spend time backtesting until you've mastered your strategy and are consistently profitable before
        resetting or starting another challenge. 
      </p>
      {/* Add this just above the close button area in maxLossLimitBlown */}
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">
        Trading is 80% Psychology.  Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.
      </p>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/@Psychology-Of-Profit" // <- replace with the exact channel if different
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition"
        >
          {/* Play icon */}
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Visit the Psychology of Profit Youtube Channel
          <span className="sr-only">(opens in a new tab)</span>
        </a>

       <p className="font-semibold mt-6 mb-6 text-red-700 dark:text-red-300">
        Don't give up!  You can do this!
      </p>

             </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


      {currentPopup?.id === 'lossLimitWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Daily Loss Limit Exceeded!
            </h2>
            <p className="mb-3  font-semibold text-lg text-gray-800 dark:text-gray-100">
              Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>,
              which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-400">
              Please consider stopping trading for the day to protect your account.
            </p>
            <p className="mb-6 font-semibold text-red-700 dark:text-red-400">
              You are worthy of success!  Don't give up!
            </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}


      {currentPopup?.id === "win10NoRulesPopup" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
              </svg>
              Warning - Big Wins Without Following Rules
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
              {currentPopup.data?.message
              ?.split('\n')
              .map((line, i) =>
                line.trim() === "Discipline > excitement!" ? (
                  <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
                    Discipline &gt; excitement!
                    <br />
                  </span>
                ) : (
                  <React.Fragment key={i}>
                    {renderWithTiltTooltip(line)}
                    <br />
                  </React.Fragment>
                  )
                )}
            </div>
            <button onClick={closeCurrentPopup} className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === "win10WithRulesPopup" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Congratulations – Following Your Rules!
            </h2>
            <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 whitespace-pre-line">
             {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.")
               .split('\n')
               .map((line, i) => (
                 <React.Fragment key={i}>
                   {formatThreeLossMessage(line)}
                   <br />
                 </React.Fragment>
               ))}
            </div>
            <button
              onClick={closeCurrentPopup}
              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'moodVolatilityPopup' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
              </svg>
              Mood Volatility Warning
            </h2>
           <p className="mb-4 text-gray-800 dark:text-gray-100">
             <span className="font-bold text-blue-700 dark:text-blue-300">
               Your emotional state appears to be heightened.
             </span>
           </p>
           <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
             Trading with intense emotions—whether it's excitement or stress—can lead to decisions based on feeling, not strategy. Your greatest asset is a calm and objective mind.
           </p>
           <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
             Before your next trade, journal what you're feeling and why. We recommend you pause until your focus is back on your process.
           </p>
            <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">
             If you consistently trade in a heightened emotional state, you may be using a position size, symbol, or timeframe that doesn't fit your personality.
           </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'twoLossAfterGoalWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
              </svg>
              Warning – Losses After Goal
            </h2>
            <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
                {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.")
                  .split('\n')
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {renderWithTiltTooltip(line)}
                      <br />
                    </React.Fragment>
                ))}
            </div>
            <button
              onClick={closeCurrentPopup}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              OK
            </button>
          </div>
        </div>
      )}


      {currentPopup?.id === 'overRiskWarning' && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
                  <button
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={closeCurrentPopup}
                    aria-label="Close"
                  >×</button>
                  <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
                    <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
                    </svg>
                    Over Risk Warning!
                  </h2>
                  
                  <div className="text-gray-800 dark:text-gray-100 text-left space-y-4">
                    <p>You lost more than your planned risk amount.</p>
                    
                    <div className="pl-4">
                      <p>Risk amount: <span className="font-bold text-yellow-500 dark:text-yellow-400">{formatCurrency(currentPopup.data.allowedRisk)}</span></p>
                      <p>Amount lost: <span className="font-bold text-yellow-500 dark:text-yellow-400">{formatCurrency(currentPopup.data.lossForTrade)}</span></p>
                    </div>

                    <p className="font-semibold text-gray-600 dark:text-gray-300">
                      Make sure that you are always using the proper stop loss and not moving it. If the trade is going too far against your analysis, you can always get out and get back in at the right time.
                    </p>
                    
                    <p className="font-bold text-yellow-500 dark:text-yellow-400">
                      Keep your losers small and your winners big and never marry a trade idea!
                    </p>
                  </div>

                  <button
                    onClick={closeCurrentPopup}
                    className="mt-6 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

      {currentPopup?.id === 'drawdownWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
              </svg>
              Drawdown Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-purple-700 dark:text-purple-300">
                Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
              </span>
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold">Drawdown amount: </span>
              <span className="font-bold text-purple-700 dark:text-purple-300">
                ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
              </span>
              <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
                ({currentPopup.data.drawdownPct?.toFixed(2)}%)
              </span>
            </p>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Consider pausing to review your trades and protect your capital before proceeding.
            </p>
            <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">
              No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.
            </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

{currentPopup?.id === 'consecutiveWinsPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Hot Streak!
      </h2>
      <p className="mb-3 text-gray-800 dark:text-gray-100">
             <span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span>
           </p>
           <p className="text-gray-700 font-semibold dark:text-gray-300">
             Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.
           </p>
           <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-blue-300">
             Stick to your process and keep your edge!
           </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


      {currentPopup?.id === "accountGoalReached" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={closeCurrentPopup} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" strokeWidth="2" />
              </svg>
              Account Goal Achieved!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-green-700 dark:text-green-300">Congratulations! You've reached your account goal and passed your challenge!</span>
            </p>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Your balance is <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span>.
              <br />
              Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-green-700 dark:text-green-300">You've made it to the next phase—don't change what's working. Stick to the disciplined plan that got you here.  </span>
            </p>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-green-700 dark:text-green-300">Your hard work has paid off!</span>
            </p>
            <button onClick={closeCurrentPopup} className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition">
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'lessonsLearnedStreakWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 6v6l4 2" strokeWidth="2" />
              </svg>
              Lessons Learned Streak!
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span>
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
            <button
              onClick={closeCurrentPopup}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === 'riskOfRuinWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >×</button>
            <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
              </svg>
              Risk of Ruin Warning
            </h2>
            <p className="mb-4 text-gray-800 dark:text-gray-100">
              <span className="font-bold text-red-700 dark:text-red-300">
                You have lost at least 75% of your initial balance. Based on your current risk, your account could be wiped out in just <span className="underline">{lossProjection.tradeCount}</span> consecutive losses.
              </span>
            </p>
            <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
              Consider lowering your risk per trade to protect your remaining capital.
            </p>
            <p className="mt-6 mb-6 font-semibold text-gray-700 dark:text-gray-300">
              Switch to your demo account and trade there until you are winning consistently.  
            </p>
            <p className="mt-6 mb-6 font-semibold text-red-700 dark:text-red-400">
              Don't be discouraged, you can do this!
            </p>
            <button
              onClick={closeCurrentPopup}
              className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {currentPopup?.id === "twoLossWarning" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full
                       border-2 border-yellow-500 relative animate-fadeIn
                       max-h-[85vh] overflow-visible"  // ← no clipping
          >
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >
              ×
            </button>

            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
              </svg>
              Warning - 2 Consecutive Losses!
            </h2>

            {/* Scroll only the body, not the whole modal */}
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left whitespace-pre-wrap break-words">
                {(currentPopup.data?.message ?? "You've lost 2 trades in a row.\nConsider taking a break and reviewing your trades.")
                  .split(/\r?\n/)
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {highlightHundredPercent(line)}
                      <br />
                    </React.Fragment>
                  ))}
              </div>
              {/* ...anything else in the body... */}
            </div>

            <button
              onClick={closeCurrentPopup}
              className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
            >
              Close
            </button>
          </div>
        </div>
      )}


      {currentPopup?.id === 'threeLossWarning' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="three-loss-title"
            aria-describedby="three-loss-desc"
          >
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={closeCurrentPopup}
              aria-label="Close"
            >
              ×
            </button>

            <h2
              id="three-loss-title"
              className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
            >
              <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Danger!
            </h2>

            <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
              {currentPopup.data?.message.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {formatThreeLossMessage(line)}
                  <br />
                </React.Fragment>
              ))}

              <div className="mt-4 text-blue-400 font-bold">
                {"You got this!!"}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              {currentPopup.data?.videoLink && (
                <a
                  href={currentPopup.data.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
                >
                  {/* Play icon */}
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6 4l10 6-10 6V4z" />
                  </svg>
                  Watch: Dealing with Trading Losses
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              )}

              <button
                onClick={closeCurrentPopup}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Required Fields */}
      {missingFields.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
            <button className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" onClick={() => setMissingFields([])} aria-label="Close">
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
              <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2" />
              </svg>
              Required Fields Missing
            </h2>
            <ul className="mb-4 text-gray-800 dark:text-gray-200">
              {missingFields.map(f => (
                <li key={f} className="pl-2">
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => setMissingFields([])} className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow">
              Close
            </button>
          </div>
        </div>
      )}

      {/* Add this near your other popups */}
      <FirstTradeAnalysisPopup 
        currentPopup={currentPopup} 
        onClose={closeCurrentPopup} 
      />

      {showChecklist && (
        <PreTradeChecklistModal
          onClose={handleChecklistConfirm}
          onConfirm={handleChecklistConfirm}
        />
      )}

      {/* --- ADD THIS FLOATING ACTION BUTTON AND CALCULATOR --- */}
          <button
            onClick={() => setShowCalculator(true)}
            title="Open Calculator"
            className="fixed bottom-12 right-6 z-40 bg-gray-800 dark:bg-gray-700 text-white rounded-full p-4 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-transform transform hover:scale-110"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
          </button>

{/* Strategy Checklist Floating Panel */}
      {showStrategyModal && (
        <div className="fixed top-24 right-8 z-40 animate-fadeIn pointer-events-auto w-96 max-w-[calc(100vw-2rem)] shadow-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border-2 border-blue-500 relative max-h-[80vh] flex flex-col">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setShowStrategyModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2 shrink-0">
              <FaClipboardList className="text-blue-500" />
              Strategy Checklist
            </h2>
            
            <div className="overflow-y-auto mb-4 pr-2 flex-1">
              {userPrefs?.tradingRules ? (
                <div className="space-y-3">
                  {userPrefs.tradingRules.split('\n').filter(rule => rule.trim() !== '').map((rule, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition">
                      <input 
                        type="checkbox" 
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" 
                      />
                      <span className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed text-sm">
                        {rule}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No Trading Rules defined yet.</p>
                  <p className="text-sm">Head over to the Lessons page to define your core strategy.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setShowStrategyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

          
          {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
          {/* --- END OF NEW CODE --- */}
      </div>
  );
}